import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AlertTitle,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  Translate as TranslateIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  Map as MapIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  AttachMoney as CashIcon,
  CreditCard as CardIcon,
  QrCode as QrIcon,
  AccountBalance as TransferIcon,
  MoreHoriz as OtherIcon,
  TableBar as TableIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc, Timestamp, collection, getDocs, deleteField, query, where, addDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, functions } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { BusinessType, WorkingHours, DaySchedule, PaymentMethodSetting } from '../../types';
import BusinessHoursV2 from '../../components/BusinessHoursV2';
import BusinessCalendar from '../../components/BusinessCalendar';
import MasterDataSelector from '../../components/MasterDataSelector';
import imageCompression from 'browser-image-compression';
import { evaluatePlan, PLAN_LABELS, TIER_DESCRIPTIONS, FREE_SLIP_LIMIT, STANDARD_SLIP_LIMIT } from '../../utils/planGate';
import { fetchBusinessCategories } from '../../utils/businessCategories';

import { resolveBoundaryHour } from '../../utils/businessDate';
import { pctToRate } from '../../utils/numberGuard';
import { customerShopUrl, bookingOrigin } from '../../utils/siteUrls';
interface Announcement {
  title: string;
  titleEn?: string;
  titleTh?: string;
  titleZh?: string;
  titleKo?: string;
  content: string;
  contentEn?: string;
  contentTh?: string;
  contentZh?: string;
  contentKo?: string;
  date?: string;
}

interface ShopTable {
  id: string;
  shopId: string;
  tableNumber: number;
  tableName: string;
  capacity: number;
  maxCapacity?: number;
  seats?: number;
  isActive: boolean;
  description?: string;
  createdAt?: any;
  updatedAt?: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ShopSettingsUnified: React.FC = () => {
  const { shop } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [tabValue, setTabValue] = useState(0);

  const defaultBusinessSchedule: {
    monday: DaySchedule;
    tuesday: DaySchedule;
    wednesday: DaySchedule;
    thursday: DaySchedule;
    friday: DaySchedule;
    saturday: DaySchedule;
    sunday: DaySchedule;
  } = {
    monday: { isOpen: true, timeSlots: [{ start: '09:00', end: '18:00' }] },
    tuesday: { isOpen: true, timeSlots: [{ start: '09:00', end: '18:00' }] },
    wednesday: { isOpen: true, timeSlots: [{ start: '09:00', end: '18:00' }] },
    thursday: { isOpen: true, timeSlots: [{ start: '09:00', end: '18:00' }] },
    friday: { isOpen: true, timeSlots: [{ start: '09:00', end: '18:00' }] },
    saturday: { isOpen: true, timeSlots: [{ start: '09:00', end: '18:00' }] },
    sunday: { isOpen: false, timeSlots: [] },
  };

  // Basic shop info
  const [formData, setFormData] = useState({
    shopName: '',
    shopNameEn: '',
    shopNameTh: '',
    shopNameZh: '',
    shopNameKo: '',
    businessTypeCode: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    // Unified address (single field)
    fullAddress: '',
    fullAddressEn: '',
    fullAddressTh: '',
    fullAddressZh: '',
    fullAddressKo: '',
    phoneNumber: '',
    email: '',
    websiteUrl: '',
    // SNS links
    facebookUrl: '',
    instagramUrl: '',
    xTwitterUrl: '',
    description: '',
    descriptionEn: '',
    descriptionTh: '',
    descriptionZh: '',
    descriptionKo: '',
    currency: 'JPY',
    timezone: 'Asia/Tokyo',
    businessDayBoundaryTime: 0,
    // 店舗詳細情報
    smokingPolicy: '' as 'non_smoking' | 'smoking' | 'separated' | '',
    parking: { available: false, details: '' } as { available: boolean; details?: string },
    // creditCard/cashlessPayment は支払い方法タブ（paymentMethods）に統合済み
    wifi: false,
    facilities: [] as string[],
  });

  // Homepage settings
  const [homepageData, setHomepageData] = useState({
    mediaType: 'image' as 'image' | 'video',
    heroImage: '',
    heroImages: [] as string[],
    heroVideo: '',
    description: '',
    seoKeywords: [] as string[],
    seoKeywordsEn: [] as string[],
    seoKeywordsTh: [] as string[],
    seoKeywordsZh: [] as string[],
    seoKeywordsKo: [] as string[],
    showBusinessHours: true,
    showPhoneNumber: true,
    showMenus: true,
    showStaff: true,
    googleMapUrl: '',
    announcements: [] as Announcement[],
    themeColors: {} as {
      primaryColor?: string;
      bgDefault?: string;
      bgWhite?: string;
      textPrimary?: string;
      textSecondary?: string;
      textMuted?: string;
      borderColor?: string;
      successColor?: string;
      warningColor?: string;
      errorColor?: string;
      bgImage?: string;
    },
    defaultProductImage: '',
  });

  const [businessSchedule, setBusinessSchedule] = useState(defaultBusinessSchedule);
  const [businessCalendar, setBusinessCalendar] = useState<{
    specialDates?: { date: string; type: 'closed' | 'special_hours'; label?: string; hours?: { start: string; end: string } }[];
  }>({ specialDates: [] });
  const [translating, setTranslating] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newKeywordEn, setNewKeywordEn] = useState('');
  const [newKeywordTh, setNewKeywordTh] = useState('');
  const [newKeywordZh, setNewKeywordZh] = useState('');
  const [newKeywordKo, setNewKeywordKo] = useState('');
  const [translatingAnnouncement, setTranslatingAnnouncement] = useState<number | null>(null);
  const [showAnnouncementTranslations, setShowAnnouncementTranslations] = useState<{ [key: number]: boolean }>({});

  // スタッフアプリ設定
  const [staffAppSettings, setStaffAppSettings] = useState({
    shiftChangeRequestEnabled: true,
    salesVisibilityDays: 0, // 0 = 制限なし
    showAnalyticsToStaff: true,
    showBackToStaff: true,
  });

  // 原価・バック設定
  const [costBackSettings, setCostBackSettings] = useState({
    enabled: false,
    defaultBackRate: 50,  // パーセント表示（内部では0.5として保存）
    showCostInAnalytics: true,
    showBackInAnalytics: true,
  });

  // v3.0.0: 売上分配の税基準
  const [salesAttributionTaxBase, setSalesAttributionTaxBase] =
    useState<'taxIncluded' | 'taxExcluded'>('taxExcluded');
  // v3.0.0: 締日（数値1-31 or 'monthEnd'、UIでは "monthEnd" or 数値文字列で扱う）
  const [salesPeriodClosingDay, setSalesPeriodClosingDay] = useState<number | 'monthEnd'>('monthEnd');
  // v3.0.0: 領収書削除保護
  const [receiptDeleteProtection, setReceiptDeleteProtection] = useState<boolean>(true);
  // v4.2.0: 客が自分のスマホから注文できるか。夜職は既定でオフ。
  const [customerOrderEnabled, setCustomerOrderEnabled] = useState<boolean>(false);
  // v4.0.0: 勤怠の猶予。0 にすると1分の遅れでも遅刻として記録される
  const [attendanceSettings, setAttendanceSettings] = useState({
    lateGraceMinutes: 5,
    earlyLeaveGraceMinutes: 15,
  });
  // v4.0.0: 伝票の取消ポリシー
  const [voidPolicy, setVoidPolicy] = useState({
    allowedRoles: ['owner', 'manager'] as ('owner' | 'manager' | 'staff')[],
    requireApproval: false,
    timeLimitMinutes: null as number | null,
    allowAfterReceipt: false,
    requireNote: true,
    allowAfterDailyClose: false,
  });
  // v4.0.0: バック調整のポリシー
  const [adjustmentPolicy, setAdjustmentPolicy] = useState({
    allowedRoles: ['owner', 'manager'] as ('owner' | 'manager')[],
    maxDeltaPerAdjustment: null as number | null,
    maxRatioOfCalculated: null as number | null,
    requireReason: true,
    allowAfterPeriodClose: false,
    visibleToStaff: true,
  });
  // v4.0.0: 課金判定に使う利用状況
  // 課金は伝票数（会計の件数）で決まる。端末では分けない。
  const [planUsage, setPlanUsage] = useState({
    slipsThisMonth: 0, peakMonthlySlips: 0, employeeCount: 0, shopCount: 1,
  });

  // インセンティブ設定
  const [incentiveSettings, setIncentiveSettings] = useState({
    enabled: false,
    // 累進の当て方。既定は retroactive（伝票の切り方で総額が変わらない）
    mode: 'retroactive' as 'retroactive' | 'marginal' | 'wholeAtTier',
    partTimeRules: {
      dailyAvgThreshold: 30000,
      lowRate: 15,       // パーセント表示
      highRate: 20,
      belowWageRate: 10,
      standardDailyHours: 6,   // 夜職の実働に合わせる（旧実装は8時間固定だった）
    },
    regularRules: {
      belowSalaryRate: 10,
      aboveSalaryRate: 15,
      tier1Threshold: 600000,
      tier1Rate: 20,
      tier2Threshold: 1000000,
      tier2Rate: 25,
    },
  });

  // シフト自動公開設定
  const [shiftAutoPublish, setShiftAutoPublish] = useState({
    enabled: false,
    scheduleType: 'weekly' as 'weekly' | 'biweekly' | 'specific_day' | 'month_end',
    dayOfWeek: 1,       // 月曜
    dayOfMonth: 1,
    biweeklyStartDate: '',
    generateDays: 7,
    notifyStaff: true,
  });

  // GPS座標
  const [shopCoordinates, setShopCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);

  // Master Data
  const [location, setLocation] = useState<{ countryCode: string; regionId: string; areaId: string } | undefined>(undefined);
  const [businessTypesData, setBusinessTypesData] = useState<{ categories: string[]; subcategories: string[]; detailedTypes: string[] } | undefined>(undefined);

  // 支払い方法設定
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodSetting[]>([]);
  const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState<PaymentMethodSetting | null>(null);

  // テーブル/座席設定
  const [shopTables, setShopTables] = useState<ShopTable[]>([]);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<ShopTable | null>(null);
  const [tablesLoading, setTablesLoading] = useState(false);

  // Image upload dialog
  const [showSizeDialog, setShowSizeDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [selectedSize, setSelectedSize] = useState<'original' | 'standard' | 'high'>('standard');

  useEffect(() => {
    if (shop) {
      // Combine address fields into unified address
      const fullAddress = [shop.prefecture, shop.city, shop.address, shop.building].filter(Boolean).join(' ');
      const fullAddressEn = [shop.prefectureEn, shop.cityEn, shop.addressEn, shop.buildingEn].filter(Boolean).join(' ');
      const fullAddressTh = [shop.prefectureTh, shop.cityTh, shop.addressTh, shop.buildingTh].filter(Boolean).join(' ');

      setFormData({
        shopName: shop.shopName,
        shopNameEn: shop.shopNameEn || '',
        shopNameTh: shop.shopNameTh || '',
        shopNameZh: shop.shopNameZh || '',
        shopNameKo: shop.shopNameKo || '',
        businessTypeCode: shop.businessTypeCode ?? '',
        ownerName: shop.ownerName ?? '',
        ownerEmail: shop.ownerEmail ?? '',
        ownerPhone: shop.ownerPhone ?? '',
        fullAddress: shop.fullAddress || fullAddress,
        fullAddressEn: shop.fullAddressEn || fullAddressEn,
        fullAddressTh: shop.fullAddressTh || fullAddressTh,
        fullAddressZh: shop.fullAddressZh || '',
        fullAddressKo: shop.fullAddressKo || '',
        phoneNumber: shop.phoneNumber ?? '',
        email: shop.email ?? '',
        websiteUrl: shop.websiteUrl || '',
        facebookUrl: shop.facebookUrl || '',
        instagramUrl: shop.instagramUrl || '',
        xTwitterUrl: shop.xTwitterUrl || '',
        description: shop.description || '',
        descriptionEn: shop.descriptionEn || '',
        descriptionTh: shop.descriptionTh || '',
        descriptionZh: shop.descriptionZh || '',
        descriptionKo: shop.descriptionKo || '',
        currency: shop.currency || 'JPY',
        timezone: shop.timezone || 'Asia/Tokyo',
        businessDayBoundaryTime: resolveBoundaryHour(shop),
        // 店舗詳細情報（shopHomepageから読み込み）
        smokingPolicy: shop.shopHomepage?.smokingPolicy || '',
        parking: shop.shopHomepage?.parking || { available: false, details: '' },
        // creditCard/cashlessPayment は支払い方法タブ（paymentMethods）に統合済み
        wifi: shop.shopHomepage?.wifi || false,
        facilities: shop.shopHomepage?.facilities || [],
      });

      // Homepage data
      if (shop.shopHomepage) {
        setHomepageData({
          mediaType: shop.shopHomepage.mediaType || 'image',
          heroImage: shop.shopHomepage.heroImage || '',
          heroImages: shop.shopHomepage.heroImages || [],
          heroVideo: shop.shopHomepage.heroVideo || '',
          description: shop.shopHomepage.description || '',
          seoKeywords: shop.shopHomepage.seoKeywords || [],
          seoKeywordsEn: (shop.shopHomepage as any).seoKeywordsEn || [],
          seoKeywordsTh: (shop.shopHomepage as any).seoKeywordsTh || [],
          seoKeywordsZh: (shop.shopHomepage as any).seoKeywordsZh || [],
          seoKeywordsKo: (shop.shopHomepage as any).seoKeywordsKo || [],
          showBusinessHours: shop.shopHomepage.showBusinessHours !== false,
          showPhoneNumber: shop.shopHomepage.showPhoneNumber !== false,
          showMenus: shop.shopHomepage.showMenus !== false,
          showStaff: shop.shopHomepage.showStaff !== false,
          googleMapUrl: shop.shopHomepage.googleMapUrl || '',
          announcements: shop.shopHomepage.announcements || [],
          themeColors: (shop.shopHomepage as any).themeColors || {},
          defaultProductImage: (shop.shopHomepage as any).defaultProductImage || '',
        });
      }

      // Business schedule
      if (shop.businessSchedule) {
        setBusinessSchedule(shop.businessSchedule);
      } else if (shop.businessHours) {
        const convertedSchedule: typeof defaultBusinessSchedule = {} as typeof defaultBusinessSchedule;
        Object.keys(shop.businessHours).forEach((day) => {
          const hours = shop.businessHours![day as keyof typeof shop.businessHours];
          convertedSchedule[day as keyof typeof defaultBusinessSchedule] = {
            isOpen: hours.isWorking,
            timeSlots: hours.isWorking ? [{ start: hours.start, end: hours.end }] : [],
          };
        });
        setBusinessSchedule(convertedSchedule);
      }

      if (shop.businessCalendar) {
        setBusinessCalendar({ specialDates: shop.businessCalendar.specialDates || [] });
      }


      if (shop.location) {
        setLocation(shop.location);
      }
      if (shop.businessTypes) {
        setBusinessTypesData(shop.businessTypes);
      }

      // GPS座標
      if ((shop as any).latitude && (shop as any).longitude) {
        setShopCoordinates({
          latitude: (shop as any).latitude,
          longitude: (shop as any).longitude,
        });
      }

      // スタッフアプリ設定
      if ((shop as any).staffAppSettings) {
        setStaffAppSettings({
          shiftChangeRequestEnabled: (shop as any).staffAppSettings.shiftChangeRequestEnabled ?? true,
          salesVisibilityDays: (shop as any).staffAppSettings.salesVisibilityDays ?? 0,
          showAnalyticsToStaff: (shop as any).staffAppSettings.showAnalyticsToStaff ?? true,
          showBackToStaff: (shop as any).staffAppSettings.showBackToStaff ?? true,
        });
      }

      // 原価・バック設定
      if (shop.costBackSettings) {
        setCostBackSettings({
          enabled: shop.costBackSettings.enabled ?? false,
          defaultBackRate: (shop.costBackSettings.defaultBackRate ?? 0.5) * 100,  // 0.5 → 50%
          showCostInAnalytics: shop.costBackSettings.showCostInAnalytics ?? true,
          showBackInAnalytics: shop.costBackSettings.showBackInAnalytics ?? true,
        });
      }

      // v3.0.0: 売上分配 税基準 + 締日 + 領収書削除保護
      setSalesAttributionTaxBase(shop.salesAttributionTaxBase ?? 'taxExcluded');
      setSalesPeriodClosingDay(shop.salesPeriod?.closingDay ?? 'monthEnd');
      setCustomerOrderEnabled((shop as any).customerOrderEnabled === true);
      setAttendanceSettings({
        lateGraceMinutes: (shop as any).attendanceSettings?.lateGraceMinutes ?? 5,
        earlyLeaveGraceMinutes: (shop as any).attendanceSettings?.earlyLeaveGraceMinutes ?? 15,
      });
      if (shop.voidPolicy) setVoidPolicy({ ...voidPolicy, ...shop.voidPolicy });
      if (shop.adjustmentPolicy) setAdjustmentPolicy({ ...adjustmentPolicy, ...shop.adjustmentPolicy });
      setReceiptDeleteProtection(
        shop.receiptSettings?.deleteProtection?.enabled !== false
      );

      // インセンティブ設定
      if (shop.incentiveSettings) {
        const is = shop.incentiveSettings;
        setIncentiveSettings({
          enabled: is.enabled ?? false,
          mode: is.mode ?? 'retroactive',
          partTimeRules: {
            dailyAvgThreshold: is.partTimeRules?.dailyAvgThreshold ?? 30000,
            lowRate: (is.partTimeRules?.lowRate ?? 0.15) * 100,
            highRate: (is.partTimeRules?.highRate ?? 0.20) * 100,
            belowWageRate: (is.partTimeRules?.belowWageRate ?? 0.10) * 100,
            standardDailyHours: is.partTimeRules?.standardDailyHours ?? 6,
          },
          regularRules: {
            belowSalaryRate: (is.regularRules?.belowSalaryRate ?? 0.10) * 100,
            aboveSalaryRate: (is.regularRules?.aboveSalaryRate ?? 0.15) * 100,
            tier1Threshold: is.regularRules?.tier1Threshold ?? 600000,
            tier1Rate: (is.regularRules?.tier1Rate ?? 0.20) * 100,
            tier2Threshold: is.regularRules?.tier2Threshold ?? 1000000,
            tier2Rate: (is.regularRules?.tier2Rate ?? 0.25) * 100,
          },
        });
      }

      // シフト自動公開設定
      if (shop.shiftAutoPublish) {
        const sap = shop.shiftAutoPublish;
        setShiftAutoPublish({
          enabled: sap.enabled ?? false,
          scheduleType: sap.scheduleType ?? 'weekly',
          dayOfWeek: sap.dayOfWeek ?? 1,
          dayOfMonth: sap.dayOfMonth ?? 1,
          biweeklyStartDate: sap.biweeklyStartDate ?? '',
          generateDays: sap.generateDays ?? 7,
          notifyStaff: sap.notifyStaff ?? true,
        });
      }

      // 支払い方法設定
      if (shop.paymentMethods && shop.paymentMethods.length > 0) {
        setPaymentMethods(shop.paymentMethods);
      }

    }
  }, [shop]);

  // 課金判定に使う利用状況を読む。
  // 伝票数（/checkouts の件数）が基準。スタッフの人数や端末は見ない。
  useEffect(() => {
    const loadPlanUsage = async () => {
      if (!shop) return;
      try {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);

        const [empSnap, slipSnap] = await Promise.all([
          getDocs(query(
            collection(db, 'employees'),
            where('shopId', '==', shop.id),
            where('status', '==', 'active'),
          )),
          getDocs(query(
            collection(db, 'checkouts'),
            where('shopId', '==', shop.id),
            where('createdAt', '>=', Timestamp.fromDate(threeMonthsAgo)),
          )),
        ]);

        // 月ごとに数えて、当月ぶんと直近のピークを出す
        const byMonth: Record<string, number> = {};
        slipSnap.docs.forEach((d) => {
          const t = (d.data() as any).createdAt?.toDate?.();
          if (!t) return;
          const key = `${t.getFullYear()}-${t.getMonth()}`;
          byMonth[key] = (byMonth[key] ?? 0) + 1;
        });
        const thisKey = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;

        setPlanUsage({
          slipsThisMonth: byMonth[thisKey] ?? 0,
          peakMonthlySlips: Math.max(0, ...Object.values(byMonth)),
          employeeCount: empSnap.size,
          shopCount: 1,
        });
      } catch (err) {
        console.error('Error loading plan usage:', err);
      }
    };
    loadPlanUsage();
  }, [shop]);

  useEffect(() => {
    const loadBusinessTypes = async () => {
      try {
        // 実体は category_masters。'businessTypes' は存在しないコレクションで、
        // 読んでも 0 件になり、業種の欄が空のまま先へ進めなかった。
        const types = await fetchBusinessCategories();
        setBusinessTypes(types as unknown as BusinessType[]);
      } catch (err) {
        console.error('Error loading business types:', err);
      }
    };
    loadBusinessTypes();
  }, []);

  // テーブルをロード
  useEffect(() => {
    const loadTables = async () => {
      if (!shop) return;
      setTablesLoading(true);
      try {
        const q = query(
          collection(db, 'tables'),
          where('shopId', '==', shop.id),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(q);
        const tables = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            tableName: data.tableName || `テーブル${data.tableNumber}`,
            capacity: data.capacity || data.maxCapacity || data.seats || 1,
          };
        }) as ShopTable[];
        tables.sort((a, b) => a.tableNumber - b.tableNumber);
        setShopTables(tables);
      } catch (err) {
        console.error('Error loading tables:', err);
      } finally {
        setTablesLoading(false);
      }
    };
    loadTables();
  }, [shop]);

  const handleChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleTranslateShopInfo = async () => {
    if (!shop) return;

    setTranslating(true);
    setError(null);

    try {
      // タイムアウトを10分に設定
      const translateFn = httpsCallable(functions, 'translateMenuItems', { timeout: 600000 });

      const items: Array<{ type: string; id: string; name: string; description?: string }> = [];

      // 店舗名、住所、説明のいずれかがあれば翻訳対象に追加（既存の翻訳があっても再翻訳）
      if (formData.shopName) {
        items.push({ type: 'category', id: 'shopName', name: formData.shopName });
      }
      if (formData.fullAddress) {
        items.push({ type: 'category', id: 'fullAddress', name: formData.fullAddress });
      }
      if (formData.description) {
        items.push({ type: 'category', id: 'description', name: formData.description });
      }

      console.log('Items to translate:', items);

      if (items.length === 0) {
        setError('翻訳対象のテキストがありません');
        setTranslating(false);
        return;
      }

      const result = await translateFn({
        sourceLanguage: 'ja',
        targetLanguages: ['en', 'th', 'zh-TW', 'ko'],
        items,
        batchSize: 5,
        forceRetranslate: true,
      });

      console.log('Translation result:', result.data);
      const translations = (result.data as any).translations;
      console.log('Translations array:', translations);
      const newFormData = { ...formData };

      translations.forEach((t: any) => {
        console.log('Processing translation item:', t);
        if (t.id === 'shopName') {
          if (t.nameEn) newFormData.shopNameEn = t.nameEn;
          if (t.nameTh) newFormData.shopNameTh = t.nameTh;
          if (t.nameZhTw) newFormData.shopNameZh = t.nameZhTw;
          if (t.nameKo) newFormData.shopNameKo = t.nameKo;
        } else if (t.id === 'fullAddress') {
          if (t.nameEn) newFormData.fullAddressEn = t.nameEn;
          if (t.nameTh) newFormData.fullAddressTh = t.nameTh;
          if (t.nameZhTw) newFormData.fullAddressZh = t.nameZhTw;
          if (t.nameKo) newFormData.fullAddressKo = t.nameKo;
        } else if (t.id === 'description') {
          if (t.nameEn) newFormData.descriptionEn = t.nameEn;
          if (t.nameTh) newFormData.descriptionTh = t.nameTh;
          if (t.nameZhTw) newFormData.descriptionZh = t.nameZhTw;
          if (t.nameKo) newFormData.descriptionKo = t.nameKo;
        }
      });

      setFormData(newFormData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Translation error:', err);
      setError(err.message || '翻訳中にエラーが発生しました');
    } finally {
      setTranslating(false);
    }
  };

  // Image upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (type === 'image') {
      const fileArray = Array.from(files);
      const dataTransfer = new DataTransfer();
      fileArray.forEach(file => dataTransfer.items.add(file));
      setSelectedFiles(dataTransfer.files);
      setShowSizeDialog(true);
    } else {
      handleFileUpload(files, type);
    }
    event.target.value = '';
  };

  const handleConfirmSizeAndUpload = () => {
    if (selectedFiles) {
      handleFileUpload(selectedFiles, 'image');
      setShowSizeDialog(false);
      setSelectedFiles(null);
    }
  };

  const handleFileUpload = async (files: FileList, type: 'image' | 'video') => {
    if (!shop) return;

    setUploading(true);
    setError(null);

    try {
      if (type === 'image') {
        const uploadPromises = Array.from(files).map(async (file) => {
          let processedFile = file;

          if (selectedSize !== 'original') {
            const options = {
              maxSizeMB: selectedSize === 'standard' ? 0.5 : 1.0,
              maxWidthOrHeight: selectedSize === 'standard' ? 800 : 1200,
              useWebWorker: true,
              fileType: 'image/jpeg' as const,
            };

            try {
              processedFile = await imageCompression(file, options);
            } catch (compressionError) {
              console.error('圧縮エラー:', compressionError);
            }
          }

          if (processedFile.size > 50 * 1024 * 1024) {
            throw new Error(`${file.name} のファイルサイズが大きすぎます`);
          }

          const fileExtension = selectedSize === 'original' ? file.name.split('.').pop() : 'jpg';
          const fileName = `homepage/${shop.id}/hero_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExtension}`;
          const storageRef = ref(storage, fileName);

          await uploadBytes(storageRef, processedFile);
          return await getDownloadURL(storageRef);
        });

        const downloadURLs = await Promise.all(uploadPromises);
        const newHeroImages = [...homepageData.heroImages, ...downloadURLs];

        const updatedData = {
          ...homepageData,
          heroImages: newHeroImages,
          heroImage: newHeroImages[0] || '',
          heroVideo: homepageData.heroVideo || '',
          mediaType: 'image' as 'image' | 'video'
        };

        const cleanedData = Object.fromEntries(
          Object.entries(updatedData).filter(([_, v]) => v !== undefined)
        );

        const shopRef = doc(db, 'shops', shop.id);
        await updateDoc(shopRef, {
          shopHomepage: cleanedData,
          updatedAt: Timestamp.now(),
        });

        setHomepageData(updatedData);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const file = files[0];
        if (file.size > 100 * 1024 * 1024) {
          setError('動画ファイルサイズは100MB以下にしてください');
          return;
        }

        const fileExtension = file.name.split('.').pop();
        const fileName = `homepage/${shop.id}/video_${Date.now()}.${fileExtension}`;
        const storageRef = ref(storage, fileName);

        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        setHomepageData({ ...homepageData, heroVideo: downloadURL, mediaType: 'video' });
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'ファイルのアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteHeroImage = (index: number) => {
    const newHeroImages = homepageData.heroImages.filter((_, i) => i !== index);
    setHomepageData({
      ...homepageData,
      heroImages: newHeroImages,
      heroImage: newHeroImages[0] || '',
    });
  };

  const handleAddKeyword = (lang: 'ja' | 'en' | 'th' | 'zh' | 'ko' = 'ja') => {
    const fieldMap = {
      ja: 'seoKeywords',
      en: 'seoKeywordsEn',
      th: 'seoKeywordsTh',
      zh: 'seoKeywordsZh',
      ko: 'seoKeywordsKo',
    } as const;
    const inputMap = {
      ja: { value: newKeyword, setter: setNewKeyword },
      en: { value: newKeywordEn, setter: setNewKeywordEn },
      th: { value: newKeywordTh, setter: setNewKeywordTh },
      zh: { value: newKeywordZh, setter: setNewKeywordZh },
      ko: { value: newKeywordKo, setter: setNewKeywordKo },
    };
    const field = fieldMap[lang];
    const { value, setter } = inputMap[lang];
    const keywords = homepageData[field] as string[];
    if (value.trim() && !keywords.includes(value.trim())) {
      setHomepageData({
        ...homepageData,
        [field]: [...keywords, value.trim()],
      });
      setter('');
    }
  };

  const handleDeleteKeyword = (keyword: string, lang: 'ja' | 'en' | 'th' | 'zh' | 'ko' = 'ja') => {
    const fieldMap = {
      ja: 'seoKeywords',
      en: 'seoKeywordsEn',
      th: 'seoKeywordsTh',
      zh: 'seoKeywordsZh',
      ko: 'seoKeywordsKo',
    } as const;
    const field = fieldMap[lang];
    const keywords = homepageData[field] as string[];
    setHomepageData({
      ...homepageData,
      [field]: keywords.filter(k => k !== keyword),
    });
  };

  const handleAddAnnouncement = () => {
    setHomepageData({
      ...homepageData,
      announcements: [
        ...homepageData.announcements,
        { title: '', content: '', date: new Date().toISOString().split('T')[0] },
      ],
    });
  };

  const handleUpdateAnnouncement = (index: number, field: keyof Announcement, value: string) => {
    const newAnnouncements = [...homepageData.announcements];
    newAnnouncements[index] = { ...newAnnouncements[index], [field]: value };
    setHomepageData({ ...homepageData, announcements: newAnnouncements });
  };

  const handleDeleteAnnouncement = (index: number) => {
    setHomepageData({
      ...homepageData,
      announcements: homepageData.announcements.filter((_, i) => i !== index),
    });
  };

  const handleTranslateAnnouncement = async (index: number) => {
    if (!shop) return;

    const announcement = homepageData.announcements[index];
    if (!announcement.title && !announcement.content) {
      setError('翻訳するタイトルまたは内容を入力してください');
      return;
    }

    setTranslatingAnnouncement(index);
    setError(null);

    try {
      const translateFn = httpsCallable(functions, 'translateMenuItems', { timeout: 300000 });

      // タイトルと内容を翻訳
      const items = [];
      if (announcement.title) {
        items.push({ type: 'category', id: `announcement_${index}_title`, name: announcement.title, description: '' });
      }
      if (announcement.content) {
        items.push({ type: 'category', id: `announcement_${index}_content`, name: '', description: announcement.content });
      }

      const result = await translateFn({
        sourceLanguage: 'ja',
        targetLanguages: ['en', 'th', 'zh-TW', 'ko'],
        items,
        batchSize: 5,
        forceRetranslate: true,
      });

      const data = result.data as { translations: Array<{ id: string; nameEn?: string; nameTh?: string; nameZhTw?: string; nameKo?: string; descriptionEn?: string; descriptionTh?: string; descriptionZhTw?: string; descriptionKo?: string }> };

      // 翻訳結果を適用
      const newAnnouncements = [...homepageData.announcements];
      const updatedAnnouncement = { ...newAnnouncements[index] };

      data.translations.forEach(item => {
        if (item.id === `announcement_${index}_title`) {
          if (item.nameEn) updatedAnnouncement.titleEn = item.nameEn;
          if (item.nameTh) updatedAnnouncement.titleTh = item.nameTh;
          if (item.nameZhTw) updatedAnnouncement.titleZh = item.nameZhTw;
          if (item.nameKo) updatedAnnouncement.titleKo = item.nameKo;
        }
        if (item.id === `announcement_${index}_content`) {
          if (item.descriptionEn) updatedAnnouncement.contentEn = item.descriptionEn;
          if (item.descriptionTh) updatedAnnouncement.contentTh = item.descriptionTh;
          if (item.descriptionZhTw) updatedAnnouncement.contentZh = item.descriptionZhTw;
          if (item.descriptionKo) updatedAnnouncement.contentKo = item.descriptionKo;
        }
      });

      newAnnouncements[index] = updatedAnnouncement;
      setHomepageData({ ...homepageData, announcements: newAnnouncements });
      setShowAnnouncementTranslations({ ...showAnnouncementTranslations, [index]: true });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('お知らせ翻訳エラー:', err);
      setError(`翻訳エラー: ${err.message}`);
    } finally {
      setTranslatingAnnouncement(null);
    }
  };

  // 支払い方法関連のハンドラー
  const getPaymentTypeIcon = (type: string) => {
    switch (type) {
      case 'cash': return <CashIcon />;
      case 'card': return <CardIcon />;
      case 'qr': return <QrIcon />;
      case 'transfer': return <TransferIcon />;
      default: return <OtherIcon />;
    }
  };

  const getPaymentTypeLabel = (type: string) => {
    switch (type) {
      case 'cash': return '現金';
      case 'card': return 'カード';
      case 'qr': return 'QR決済';
      case 'transfer': return '振込';
      default: return 'その他';
    }
  };

  const handleOpenPaymentMethodDialog = (method?: PaymentMethodSetting) => {
    if (method) {
      setEditingPaymentMethod(method);
    } else {
      setEditingPaymentMethod({
        id: `pm_${Date.now()}`,
        code: '',
        name: '',
        nameEn: '',
        nameTh: '',
        type: 'other',
        isActive: true,
        sortOrder: paymentMethods.length + 1,
      });
    }
    setPaymentMethodDialogOpen(true);
  };

  const handleSavePaymentMethod = () => {
    if (!editingPaymentMethod) return;

    const existingIndex = paymentMethods.findIndex(m => m.id === editingPaymentMethod.id);
    if (existingIndex >= 0) {
      // 更新
      setPaymentMethods(paymentMethods.map(m => m.id === editingPaymentMethod.id ? editingPaymentMethod : m));
    } else {
      // 新規追加
      setPaymentMethods([...paymentMethods, editingPaymentMethod]);
    }
    setPaymentMethodDialogOpen(false);
    setEditingPaymentMethod(null);
  };

  const handleDeletePaymentMethod = (id: string) => {
    setPaymentMethods(paymentMethods.filter(m => m.id !== id));
  };

  const handleTogglePaymentMethod = (id: string) => {
    setPaymentMethods(paymentMethods.map(m =>
      m.id === id ? { ...m, isActive: !m.isActive } : m
    ));
  };

  const handleMovePaymentMethod = (index: number, direction: 'up' | 'down') => {
    const newMethods = [...paymentMethods];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newMethods.length) return;

    [newMethods[index], newMethods[targetIndex]] = [newMethods[targetIndex], newMethods[index]];
    // sortOrderを更新
    newMethods.forEach((m, i) => m.sortOrder = i + 1);
    setPaymentMethods(newMethods);
  };

  // テーブル関連のハンドラー
  const handleOpenTableDialog = (table?: ShopTable) => {
    if (table) {
      setEditingTable(table);
    } else {
      const maxTableNumber = shopTables.length > 0
        ? Math.max(...shopTables.map(t => t.tableNumber))
        : 0;
      setEditingTable({
        id: '',
        shopId: shop?.id || '',
        tableNumber: maxTableNumber + 1,
        tableName: `テーブル${maxTableNumber + 1}`,
        capacity: 4,
        isActive: true,
        description: '',
      });
    }
    setTableDialogOpen(true);
  };

  const handleSaveTable = async () => {
    if (!editingTable || !shop) return;

    // 人数に負の値・0が入らないようにする。
    // 入力欄の min はブラウザのヒントで、貼り付けも保存も止めない。
    if (!Number.isFinite(Number(editingTable.capacity)) || Number(editingTable.capacity) < 1) {
      setError('席数は1以上にしてください');
      return;
    }
    setTablesLoading(true);
    try {
      if (editingTable.id) {
        // 更新
        const tableRef = doc(db, 'tables', editingTable.id);
        await updateDoc(tableRef, {
          tableNumber: editingTable.tableNumber,
          tableName: editingTable.tableName,
          capacity: editingTable.capacity,
          maxCapacity: editingTable.capacity,
          seats: editingTable.capacity,
          description: editingTable.description || '',
          updatedAt: Timestamp.now(),
        });
        setShopTables(shopTables.map(t =>
          t.id === editingTable.id ? { ...t, ...editingTable } : t
        ));
      } else {
        // 新規作成
        const newTable = {
          shopId: shop.id,
          tableNumber: editingTable.tableNumber,
          tableName: editingTable.tableName,
          capacity: editingTable.capacity,
          maxCapacity: editingTable.capacity,
          seats: editingTable.capacity,
          isActive: true,
          description: editingTable.description || '',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
        const docRef = await addDoc(collection(db, 'tables'), newTable);
        setShopTables([...shopTables, { ...newTable, id: docRef.id }].sort((a, b) => a.tableNumber - b.tableNumber));
      }
      setTableDialogOpen(false);
      setEditingTable(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving table:', err);
      setError(err.message || 'テーブルの保存に失敗しました');
    } finally {
      setTablesLoading(false);
    }
  };

  const handleDeleteTable = async (tableId: string) => {
    if (!window.confirm('このテーブルを削除しますか？')) return;

    setTablesLoading(true);
    try {
      // 論理削除（isActive = false）
      const tableRef = doc(db, 'tables', tableId);
      await updateDoc(tableRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      });
      setShopTables(shopTables.filter(t => t.id !== tableId));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error deleting table:', err);
      setError(err.message || 'テーブルの削除に失敗しました');
    } finally {
      setTablesLoading(false);
    }
  };

  // Generate Google Maps search URL from address
  const generateGoogleMapsUrl = () => {
    const address = formData.fullAddress || formData.fullAddressEn;
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  };

  // 住所からGPS座標を取得（ジオコーディング）
  const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      const key = 'AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8';
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${key}`
      );
      const data = await res.json();
      if (data.status === 'OK' && data.results.length > 0) {
        return data.results[0].geometry.location;
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shop) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const shopRef = doc(db, 'shops', shop.id);

      // 住所からGPS座標を取得
      let coordinatesUpdate: Record<string, number> = {};
      if (formData.fullAddress) {
        const coords = await geocodeAddress(formData.fullAddress);
        if (coords) {
          coordinatesUpdate = { latitude: coords.lat, longitude: coords.lng };
          setShopCoordinates({ latitude: coords.lat, longitude: coords.lng });
        }
      }

      const cleanedHomepageData = {
        ...homepageData,
        heroImage: homepageData.heroImage || '',
        heroVideo: homepageData.heroVideo || '',
        description: homepageData.description || '',
        googleMapUrl: homepageData.googleMapUrl || '',
        // 店舗詳細情報（formData から統合）
        smokingPolicy: formData.smokingPolicy || '',
        parking: formData.parking,
        // creditCard/cashlessPayment は支払い方法タブ（paymentMethods）に統合
        wifi: formData.wifi,
        facilities: formData.facilities,
      };

      const finalHomepageData = Object.fromEntries(
        Object.entries(cleanedHomepageData).filter(([_, v]) => v !== undefined)
      );

      if (formData.ownerEmail !== shop.ownerEmail) {
        const updateShop = httpsCallable(functions, 'updateShop');
        await updateShop({
          shopId: shop.id,
          shopName: formData.shopName,
          ownerName: formData.ownerName,
          ownerEmail: formData.ownerEmail,
          ownerPhone: formData.ownerPhone,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          websiteUrl: formData.websiteUrl || '',
          description: formData.description || '',
        });

        await updateDoc(shopRef, {
          shopNameEn: formData.shopNameEn || '',
          shopNameTh: formData.shopNameTh || '',
          shopNameZh: formData.shopNameZh || '',
          shopNameKo: formData.shopNameKo || '',
          fullAddress: formData.fullAddress,
          fullAddressEn: formData.fullAddressEn || '',
          fullAddressTh: formData.fullAddressTh || '',
          fullAddressZh: formData.fullAddressZh || '',
          fullAddressKo: formData.fullAddressKo || '',
          facebookUrl: formData.facebookUrl || '',
          instagramUrl: formData.instagramUrl || '',
          xTwitterUrl: formData.xTwitterUrl || '',
          descriptionEn: formData.descriptionEn || '',
          descriptionTh: formData.descriptionTh || '',
          descriptionZh: formData.descriptionZh || '',
          descriptionKo: formData.descriptionKo || '',
          currency: formData.currency,
          timezone: formData.timezone,
          businessDayBoundaryTime: formData.businessDayBoundaryTime,
          businessSchedule: businessSchedule,
          businessCalendar: businessCalendar,

          location: location || deleteField(),
          businessTypes: businessTypesData || deleteField(),
          shopHomepage: finalHomepageData,
          costBackSettings: {
            enabled: costBackSettings.enabled,
            defaultBackRate: pctToRate(costBackSettings.defaultBackRate),  // 50% → 0.5
            showCostInAnalytics: costBackSettings.showCostInAnalytics,
            showBackInAnalytics: costBackSettings.showBackInAnalytics,
          },
          incentiveSettings: {
            enabled: incentiveSettings.enabled,
            mode: incentiveSettings.mode,
            partTimeRules: {
              dailyAvgThreshold: incentiveSettings.partTimeRules.dailyAvgThreshold,
              lowRate: pctToRate(incentiveSettings.partTimeRules.lowRate),
              highRate: pctToRate(incentiveSettings.partTimeRules.highRate),
              belowWageRate: pctToRate(incentiveSettings.partTimeRules.belowWageRate),
              standardDailyHours: incentiveSettings.partTimeRules.standardDailyHours,
            },
            regularRules: {
              belowSalaryRate: pctToRate(incentiveSettings.regularRules.belowSalaryRate),
              aboveSalaryRate: pctToRate(incentiveSettings.regularRules.aboveSalaryRate),
              tier1Threshold: incentiveSettings.regularRules.tier1Threshold,
              tier1Rate: pctToRate(incentiveSettings.regularRules.tier1Rate),
              tier2Threshold: incentiveSettings.regularRules.tier2Threshold,
              tier2Rate: pctToRate(incentiveSettings.regularRules.tier2Rate),
            },
          },
          shiftAutoPublish: shiftAutoPublish,
          staffAppSettings: staffAppSettings,
          paymentMethods: paymentMethods,
          // v3.0.0: 売上分配 + 締日 + 領収書削除保護
          salesAttributionTaxBase,
          salesPeriod: { closingDay: salesPeriodClosingDay },
          attendanceSettings: {
            lateGraceMinutes: attendanceSettings.lateGraceMinutes,
            earlyLeaveGraceMinutes: attendanceSettings.earlyLeaveGraceMinutes,
          },
          voidPolicy,
          adjustmentPolicy,
          customerOrderEnabled,
          'receiptSettings.deleteProtection.enabled': receiptDeleteProtection,
          ...coordinatesUpdate,
          updatedAt: Timestamp.now(),
        });
      } else {
        await updateDoc(shopRef, {
          shopName: formData.shopName,
          shopNameEn: formData.shopNameEn || '',
          shopNameTh: formData.shopNameTh || '',
          shopNameZh: formData.shopNameZh || '',
          shopNameKo: formData.shopNameKo || '',
          businessTypeCode: formData.businessTypeCode,
          ownerName: formData.ownerName,
          ownerEmail: formData.ownerEmail,
          ownerPhone: formData.ownerPhone,
          fullAddress: formData.fullAddress,
          fullAddressEn: formData.fullAddressEn || '',
          fullAddressTh: formData.fullAddressTh || '',
          fullAddressZh: formData.fullAddressZh || '',
          fullAddressKo: formData.fullAddressKo || '',
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          websiteUrl: formData.websiteUrl || '',
          facebookUrl: formData.facebookUrl || '',
          instagramUrl: formData.instagramUrl || '',
          xTwitterUrl: formData.xTwitterUrl || '',
          description: formData.description || '',
          descriptionEn: formData.descriptionEn || '',
          descriptionTh: formData.descriptionTh || '',
          descriptionZh: formData.descriptionZh || '',
          descriptionKo: formData.descriptionKo || '',
          currency: formData.currency,
          timezone: formData.timezone,
          businessDayBoundaryTime: formData.businessDayBoundaryTime,
          businessSchedule: businessSchedule,
          businessCalendar: businessCalendar,

          location: location || deleteField(),
          businessTypes: businessTypesData || deleteField(),
          shopHomepage: finalHomepageData,
          costBackSettings: {
            enabled: costBackSettings.enabled,
            defaultBackRate: pctToRate(costBackSettings.defaultBackRate),  // 50% → 0.5
            showCostInAnalytics: costBackSettings.showCostInAnalytics,
            showBackInAnalytics: costBackSettings.showBackInAnalytics,
          },
          incentiveSettings: {
            enabled: incentiveSettings.enabled,
            mode: incentiveSettings.mode,
            partTimeRules: {
              dailyAvgThreshold: incentiveSettings.partTimeRules.dailyAvgThreshold,
              lowRate: pctToRate(incentiveSettings.partTimeRules.lowRate),
              highRate: pctToRate(incentiveSettings.partTimeRules.highRate),
              belowWageRate: pctToRate(incentiveSettings.partTimeRules.belowWageRate),
              standardDailyHours: incentiveSettings.partTimeRules.standardDailyHours,
            },
            regularRules: {
              belowSalaryRate: pctToRate(incentiveSettings.regularRules.belowSalaryRate),
              aboveSalaryRate: pctToRate(incentiveSettings.regularRules.aboveSalaryRate),
              tier1Threshold: incentiveSettings.regularRules.tier1Threshold,
              tier1Rate: pctToRate(incentiveSettings.regularRules.tier1Rate),
              tier2Threshold: incentiveSettings.regularRules.tier2Threshold,
              tier2Rate: pctToRate(incentiveSettings.regularRules.tier2Rate),
            },
          },
          shiftAutoPublish: shiftAutoPublish,
          staffAppSettings: staffAppSettings,
          paymentMethods: paymentMethods,
          // v3.0.0: 売上分配 + 締日 + 領収書削除保護
          salesAttributionTaxBase,
          salesPeriod: { closingDay: salesPeriodClosingDay },
          'receiptSettings.deleteProtection.enabled': receiptDeleteProtection,
          ...coordinatesUpdate,
          updatedAt: Timestamp.now(),
          // ここから下の3項目は、以前は「オーナーのメールを変えたときだけ通る」
          // もう一方の updateDoc にしか書かれていなかった。
          // 通常の保存では、遅刻・早退の猶予と勤怠の設定が一切保存されず、
          // 成功メッセージだけ出て画面を開き直すと元に戻っていた。
          // 保存するキーを2箇所に分けて書くと必ずこうなる。
          attendanceSettings: {
            lateGraceMinutes: attendanceSettings.lateGraceMinutes,
            earlyLeaveGraceMinutes: attendanceSettings.earlyLeaveGraceMinutes,
          },});
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error updating shop:', err);
      setError(err.message || '店舗情報の更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (!shop) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>店舗情報を読み込み中...</Typography>
      </Container>
    );
  }

  const customerPageUrl = customerShopUrl(shop.shopCode);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          店舗設定
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            href={customerPageUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            プレビュー
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? '保存中...' : '保存する'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          設定を更新しました
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="基本情報" />
          <Tab label="ホームページ" />
          <Tab label="原価・バック" />
          <Tab label="支払い方法" />
          <Tab label="営業時間" />
        </Tabs>

        {/* Tab 0: Basic Info */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              店舗名・説明
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={translating ? <CircularProgress size={20} /> : <TranslateIcon />}
              onClick={handleTranslateShopInfo}
              disabled={translating || !formData.shopName}
            >
              {translating ? '翻訳中...' : 'AI翻訳（5言語）'}
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="店舗名（日本語）"
                value={formData.shopName}
                onChange={handleChange('shopName')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗名（English）"
                value={formData.shopNameEn}
                onChange={handleChange('shopNameEn')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗名（ไทย）"
                value={formData.shopNameTh}
                onChange={handleChange('shopNameTh')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗名（繁體中文）"
                value={formData.shopNameZh}
                onChange={handleChange('shopNameZh')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗名（한국어）"
                value={formData.shopNameKo}
                onChange={handleChange('shopNameKo')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="業種"
                value={formData.businessTypeCode}
                onChange={handleChange('businessTypeCode')}
                required
              >
                {businessTypes.map(type => (
                  <MenuItem key={type.id} value={type.code}>
                    {type.icon} {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="店舗説明（日本語）"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleChange('description')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗説明（English）"
                multiline
                rows={4}
                value={formData.descriptionEn}
                onChange={handleChange('descriptionEn')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗説明（ไทย）"
                multiline
                rows={4}
                value={formData.descriptionTh}
                onChange={handleChange('descriptionTh')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗説明（繁體中文）"
                multiline
                rows={4}
                value={formData.descriptionZh}
                onChange={handleChange('descriptionZh')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗説明（한국어）"
                multiline
                rows={4}
                value={formData.descriptionKo}
                onChange={handleChange('descriptionKo')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            マスタデータ設定（ポータル検索用）
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            店舗の所在地と業種分類を選択してください。これらの情報はポータルサイトでの検索に使用されます。
          </Typography>

          <MasterDataSelector
            countryCode={location?.countryCode}
            regionId={location?.regionId}
            areaId={location?.areaId}
            onLocationChange={setLocation}
            categories={businessTypesData?.categories || []}
            subcategories={businessTypesData?.subcategories || []}
            detailedTypes={businessTypesData?.detailedTypes || []}
            onBusinessTypesChange={setBusinessTypesData}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            オーナー情報
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="オーナー名"
                value={formData.ownerName}
                onChange={handleChange('ownerName')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="オーナーメールアドレス"
                type="email"
                value={formData.ownerEmail}
                onChange={handleChange('ownerEmail')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="オーナー電話番号"
                value={formData.ownerPhone}
                onChange={handleChange('ownerPhone')}
                required
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            店舗所在地
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="住所（日本語または現地語）"
                  value={formData.fullAddress}
                  onChange={handleChange('fullAddress')}
                  required
                  placeholder="例: 東京都渋谷区神宮前1-2-3 ABCビル2F"
                  helperText="住所を入力して、右のボタンでGoogleマップで確認できます"
                />
                <Button
                  variant="outlined"
                  onClick={generateGoogleMapsUrl}
                  disabled={!formData.fullAddress}
                  startIcon={<MapIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  地図で確認
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="住所（English）"
                value={formData.fullAddressEn}
                onChange={handleChange('fullAddressEn')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="住所（ไทย）"
                value={formData.fullAddressTh}
                onChange={handleChange('fullAddressTh')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="住所（繁體中文）"
                value={formData.fullAddressZh}
                onChange={handleChange('fullAddressZh')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="住所（한국어）"
                value={formData.fullAddressKo}
                onChange={handleChange('fullAddressKo')}
              />
            </Grid>
          </Grid>

          {/* GPS座標表示 */}
          <Box sx={{ mb: 2, p: 2, bgcolor: shopCoordinates ? 'success.50' : 'warning.50', borderRadius: 1, border: 1, borderColor: shopCoordinates ? 'success.200' : 'warning.200' }}>
            {shopCoordinates ? (
              <Typography variant="body2" color="text.secondary">
                GPS座標: 緯度 {shopCoordinates.latitude.toFixed(6)}, 経度 {shopCoordinates.longitude.toFixed(6)}
              </Typography>
            ) : (
              <Typography variant="body2" color="warning.main">
                GPS出勤に必要な座標が未設定です。保存時に住所から自動取得されます。
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            連絡先情報
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗電話番号"
                value={formData.phoneNumber}
                onChange={handleChange('phoneNumber')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="店舗メールアドレス"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Webサイト"
                value={formData.websiteUrl}
                onChange={handleChange('websiteUrl')}
                placeholder="https://example.com"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            店舗詳細情報
          </Typography>

          <Box sx={{ mb: 3 }}>
            <TextField
              select
              fullWidth
              label="喫煙・禁煙"
              value={formData.smokingPolicy}
              onChange={(e) => setFormData({ ...formData, smokingPolicy: e.target.value as any })}
            >
              <MenuItem value="">未設定</MenuItem>
              <MenuItem value="non_smoking">完全禁煙</MenuItem>
              <MenuItem value="separated">分煙</MenuItem>
              <MenuItem value="smoking">喫煙可</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.parking.available}
                  onChange={(e) => setFormData({
                    ...formData,
                    parking: { ...formData.parking, available: e.target.checked }
                  })}
                />
              }
              label="駐車場あり"
            />
            {formData.parking.available && (
              <TextField
                fullWidth
                label="駐車場の詳細"
                value={formData.parking.details}
                onChange={(e) => setFormData({
                  ...formData,
                  parking: { ...formData.parking, details: e.target.value }
                })}
                sx={{ mt: 2 }}
              />
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.wifi}
                  onChange={(e) => setFormData({ ...formData, wifi: e.target.checked })}
                />
              }
              label="Wi-Fiあり"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              その他の設備・サービス
            </Typography>
            <TextField
              select
              fullWidth
              label="設備・サービス"
              SelectProps={{
                multiple: true,
                value: formData.facilities,
                onChange: (e) => setFormData({ ...formData, facilities: e.target.value as string[] }),
              }}
            >
              <MenuItem value="個室あり">個室あり</MenuItem>
              <MenuItem value="貸切可">貸切可</MenuItem>
              <MenuItem value="座敷あり">座敷あり</MenuItem>
              <MenuItem value="カウンター席あり">カウンター席あり</MenuItem>
              <MenuItem value="バリアフリー">バリアフリー</MenuItem>
              <MenuItem value="キッズスペース">キッズスペース</MenuItem>
              <MenuItem value="ベビーカー入店可">ベビーカー入店可</MenuItem>
              <MenuItem value="ペット同伴可">ペット同伴可</MenuItem>
              <MenuItem value="テイクアウト可">テイクアウト可</MenuItem>
              <MenuItem value="デリバリー可">デリバリー可</MenuItem>
            </TextField>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {formData.facilities.map((facility: string) => (
                <Chip
                  key={facility}
                  label={facility}
                  onDelete={() => setFormData({
                    ...formData,
                    facilities: formData.facilities.filter((f: string) => f !== facility)
                  })}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            SNSリンク
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            入力したSNSリンクはホームページに表示されます
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Facebook URL"
                value={formData.facebookUrl}
                onChange={handleChange('facebookUrl')}
                placeholder="https://www.facebook.com/yourpage"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Instagram URL"
                value={formData.instagramUrl}
                onChange={handleChange('instagramUrl')}
                placeholder="https://www.instagram.com/youraccount"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="X (Twitter) URL"
                value={formData.xTwitterUrl}
                onChange={handleChange('xTwitterUrl')}
                placeholder="https://x.com/youraccount"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            通貨・タイムゾーン設定
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="通貨"
                value={formData.currency}
                onChange={handleChange('currency')}
              >
                <MenuItem value="JPY">JPY - 日本円 (¥)</MenuItem>
                <MenuItem value="THB">THB - タイバーツ (฿)</MenuItem>
                <MenuItem value="USD">USD - 米ドル ($)</MenuItem>
                <MenuItem value="EUR">EUR - ユーロ (€)</MenuItem>
                <MenuItem value="GBP">GBP - 英ポンド (£)</MenuItem>
                <MenuItem value="CNY">CNY - 中国元 (¥)</MenuItem>
                <MenuItem value="KRW">KRW - 韓国ウォン (₩)</MenuItem>
                <MenuItem value="TWD">TWD - 台湾ドル (NT$)</MenuItem>
                <MenuItem value="HKD">HKD - 香港ドル (HK$)</MenuItem>
                <MenuItem value="SGD">SGD - シンガポールドル (S$)</MenuItem>
                <MenuItem value="MYR">MYR - マレーシアリンギット (RM)</MenuItem>
                <MenuItem value="VND">VND - ベトナムドン (₫)</MenuItem>
                <MenuItem value="IDR">IDR - インドネシアルピア (Rp)</MenuItem>
                <MenuItem value="PHP">PHP - フィリピンペソ (₱)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="タイムゾーン"
                value={formData.timezone}
                onChange={handleChange('timezone')}
              >
                <MenuItem value="Asia/Tokyo">Asia/Tokyo - 日本標準時 (JST)</MenuItem>
                <MenuItem value="Asia/Bangkok">Asia/Bangkok - タイ標準時 (ICT)</MenuItem>
                <MenuItem value="Asia/Singapore">Asia/Singapore - シンガポール標準時 (SGT)</MenuItem>
                <MenuItem value="Asia/Hong_Kong">Asia/Hong_Kong - 香港標準時 (HKT)</MenuItem>
                <MenuItem value="Asia/Shanghai">Asia/Shanghai - 中国標準時 (CST)</MenuItem>
                <MenuItem value="Asia/Taipei">Asia/Taipei - 台湾標準時 (CST)</MenuItem>
                <MenuItem value="Asia/Seoul">Asia/Seoul - 韓国標準時 (KST)</MenuItem>
                <MenuItem value="Asia/Manila">Asia/Manila - フィリピン標準時 (PHT)</MenuItem>
                <MenuItem value="Asia/Ho_Chi_Minh">Asia/Ho_Chi_Minh - ベトナム標準時 (ICT)</MenuItem>
                <MenuItem value="Asia/Jakarta">Asia/Jakarta - インドネシア西部標準時 (WIB)</MenuItem>
                <MenuItem value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur - マレーシア標準時 (MYT)</MenuItem>
                <MenuItem value="Europe/London">Europe/London - グリニッジ標準時 (GMT)</MenuItem>
                <MenuItem value="Europe/Paris">Europe/Paris - 中央ヨーロッパ時間 (CET)</MenuItem>
                <MenuItem value="America/New_York">America/New_York - 米国東部時間 (EST)</MenuItem>
                <MenuItem value="America/Los_Angeles">America/Los_Angeles - 米国太平洋時間 (PST)</MenuItem>
                <MenuItem value="Pacific/Honolulu">Pacific/Honolulu - ハワイ標準時 (HST)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="営業日区切り時刻"
                value={formData.businessDayBoundaryTime}
                onChange={(e) => setFormData({ ...formData, businessDayBoundaryTime: Number(e.target.value) })}
                helperText="この時刻より前の取引は前営業日として集計されます（深夜営業の場合に設定）"
              >
                <MenuItem value={0}>0:00（深夜0時区切り・デフォルト）</MenuItem>
                <MenuItem value={1}>1:00</MenuItem>
                <MenuItem value={2}>2:00</MenuItem>
                <MenuItem value={3}>3:00</MenuItem>
                <MenuItem value={4}>4:00</MenuItem>
                <MenuItem value={5}>5:00</MenuItem>
                <MenuItem value={6}>6:00（朝6時区切り）</MenuItem>
                <MenuItem value={7}>7:00</MenuItem>
                <MenuItem value={8}>8:00</MenuItem>
                <MenuItem value={9}>9:00</MenuItem>
                <MenuItem value={10}>10:00</MenuItem>
                <MenuItem value={11}>11:00</MenuItem>
                <MenuItem value={12}>12:00</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              店舗コード
            </Typography>
            <Typography variant="h3" color="primary" gutterBottom>
              {shop.shopCode}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ※ 店舗コードは変更できません。お客様がこのコードで予約します。
            </Typography>
          </Paper>

          <Divider sx={{ my: 3 }} />

          {/* テーブル/座席設定 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                <TableIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                テーブル/座席マスタ
              </Typography>
              <Typography variant="body2" color="text.secondary">
                店舗のテーブルや座席を管理します。QRコード印刷やオーダー管理に使用されます。
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenTableDialog()}
              disabled={tablesLoading}
            >
              テーブル追加
            </Button>
          </Box>

          {tablesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : shopTables.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <TableIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary" gutterBottom>
                テーブルが登録されていません
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenTableDialog()}
                sx={{ mt: 2 }}
              >
                最初のテーブルを追加
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>番号</TableCell>
                    <TableCell>テーブル名</TableCell>
                    <TableCell align="center">定員</TableCell>
                    <TableCell>備考</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shopTables.map((table) => (
                    <TableRow key={table.id}>
                      <TableCell>
                        <Chip label={table.tableNumber} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">{table.tableName}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{table.capacity}名</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {table.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenTableDialog(table)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTable(table.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              ここで設定したテーブルは、スタッフアプリのテーブル管理画面やモバイルオーダーで使用されます。
              予約用の座席（個室など）は別途「予約設定」から設定してください。
            </Typography>
          </Alert>
        </TabPanel>

        {/* Tab 1: Homepage */}
        <TabPanel value={tabValue} index={1}>
          {/* Hero Image/Video */}
          <Typography variant="h6" gutterBottom>
            ヒーロー画像・動画
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ページの最上部に表示される大きな画像または動画です
          </Typography>

          <Box sx={{ mb: 3 }}>
            <TextField
              select
              fullWidth
              label="メディアタイプ"
              value={homepageData.mediaType ?? 'image'}
              onChange={(e) => setHomepageData({ ...homepageData, mediaType: e.target.value as 'image' | 'video' })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="image">画像</MenuItem>
              <MenuItem value="video">動画</MenuItem>
            </TextField>

            {homepageData.mediaType === 'image' ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  複数の画像をアップロードすると、自動的にカルーセル（スライドショー）表示されます
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                  disabled={uploading}
                  fullWidth
                >
                  {uploading ? 'アップロード中...' : 'ヒーロー画像をアップロード（複数選択可）'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={(e) => handleFileSelect(e, 'image')}
                  />
                </Button>

                {homepageData.heroImages.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      登録済み画像 ({homepageData.heroImages.length}枚)
                    </Typography>
                    <Grid container spacing={2}>
                      {homepageData.heroImages.map((imageUrl, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Box sx={{ position: 'relative' }}>
                            <Box
                              component="img"
                              src={imageUrl}
                              alt={`Hero ${index + 1}`}
                              sx={{
                                width: '100%',
                                height: 150,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '2px solid',
                                borderColor: index === 0 ? 'primary.main' : 'divider',
                              }}
                            />
                            {index === 0 && (
                              <Chip
                                label="メイン"
                                color="primary"
                                size="small"
                                sx={{ position: 'absolute', top: 4, left: 4 }}
                              />
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteHeroImage(index)}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'error.light', color: 'white' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                  disabled={uploading}
                  fullWidth
                >
                  {uploading ? 'アップロード中...' : 'ヒーロー動画をアップロード'}
                  <input
                    type="file"
                    hidden
                    accept="video/*"
                    onChange={(e) => handleFileSelect(e, 'video')}
                  />
                </Button>
                {homepageData.heroVideo && (
                  <Box sx={{ mt: 2 }}>
                    <video
                      src={homepageData.heroVideo}
                      controls
                      style={{ width: '100%', maxHeight: '300px', borderRadius: '8px' }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Announcements */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                お知らせ
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddAnnouncement}
              >
                追加
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ホームページに表示されるお知らせです。自動でスライド表示されます。
            </Typography>

            {homepageData.announcements.map((announcement, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={translatingAnnouncement === index ? <CircularProgress size={16} /> : <TranslateIcon />}
                    onClick={() => handleTranslateAnnouncement(index)}
                    disabled={translatingAnnouncement !== null || (!announcement.title && !announcement.content)}
                  >
                    {translatingAnnouncement === index ? '翻訳中...' : 'AI翻訳'}
                  </Button>
                  <IconButton size="small" color="error" onClick={() => handleDeleteAnnouncement(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="タイトル（日本語）"
                      value={announcement.title ?? ''}
                      onChange={(e) => handleUpdateAnnouncement(index, 'title', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="内容（日本語）"
                      value={announcement.content ?? ''}
                      onChange={(e) => handleUpdateAnnouncement(index, 'content', e.target.value)}
                    />
                  </Grid>

                  {/* 翻訳フィールド表示トグル */}
                  {(showAnnouncementTranslations[index] || announcement.titleEn || announcement.contentEn) && (
                    <>
                      <Grid item xs={12}>
                        <Button
                          size="small"
                          onClick={() => setShowAnnouncementTranslations({
                            ...showAnnouncementTranslations,
                            [index]: !showAnnouncementTranslations[index]
                          })}
                        >
                          {showAnnouncementTranslations[index] ? '翻訳を隠す' : '翻訳を表示'}
                        </Button>
                      </Grid>

                      {showAnnouncementTranslations[index] && (
                        <>
                          {/* 英語 */}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="タイトル（English）"
                              value={announcement.titleEn || ''}
                              onChange={(e) => handleUpdateAnnouncement(index, 'titleEn', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              rows={2}
                              label="内容（English）"
                              value={announcement.contentEn || ''}
                              onChange={(e) => handleUpdateAnnouncement(index, 'contentEn', e.target.value)}
                            />
                          </Grid>

                          {/* タイ語 */}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="タイトル（ไทย）"
                              value={announcement.titleTh || ''}
                              onChange={(e) => handleUpdateAnnouncement(index, 'titleTh', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              rows={2}
                              label="内容（ไทย）"
                              value={announcement.contentTh || ''}
                              onChange={(e) => handleUpdateAnnouncement(index, 'contentTh', e.target.value)}
                            />
                          </Grid>

                          {/* 繁体字中国語 */}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="タイトル（繁體中文）"
                              value={announcement.titleZh || ''}
                              onChange={(e) => handleUpdateAnnouncement(index, 'titleZh', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              rows={2}
                              label="内容（繁體中文）"
                              value={announcement.contentZh || ''}
                              onChange={(e) => handleUpdateAnnouncement(index, 'contentZh', e.target.value)}
                            />
                          </Grid>

                          {/* 韓国語 */}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="タイトル（한국어）"
                              value={announcement.titleKo || ''}
                              onChange={(e) => handleUpdateAnnouncement(index, 'titleKo', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              rows={2}
                              label="内容（한국어）"
                              value={announcement.contentKo || ''}
                              onChange={(e) => handleUpdateAnnouncement(index, 'contentKo', e.target.value)}
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              </Paper>
            ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* SEO Keywords */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              SEOキーワード
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              各言語でサイトに表示されるSEOキーワードを設定します。
            </Typography>

            {/* 日本語キーワード */}
            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>日本語</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="キーワード（日本語）"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddKeyword('ja');
                  }
                }}
              />
              <Button variant="outlined" onClick={() => handleAddKeyword('ja')} disabled={!newKeyword.trim()}>
                追加
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {homepageData.seoKeywords.map((keyword) => (
                <Chip key={keyword} label={keyword} onDelete={() => handleDeleteKeyword(keyword, 'ja')} />
              ))}
            </Box>

            {/* 英語キーワード */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>English</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Keywords (English)"
                value={newKeywordEn}
                onChange={(e) => setNewKeywordEn(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddKeyword('en');
                  }
                }}
              />
              <Button variant="outlined" onClick={() => handleAddKeyword('en')} disabled={!newKeywordEn.trim()}>
                追加
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {homepageData.seoKeywordsEn.map((keyword) => (
                <Chip key={keyword} label={keyword} onDelete={() => handleDeleteKeyword(keyword, 'en')} />
              ))}
            </Box>

            {/* タイ語キーワード */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>ไทย</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="คำหลัก (ไทย)"
                value={newKeywordTh}
                onChange={(e) => setNewKeywordTh(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddKeyword('th');
                  }
                }}
              />
              <Button variant="outlined" onClick={() => handleAddKeyword('th')} disabled={!newKeywordTh.trim()}>
                追加
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {homepageData.seoKeywordsTh.map((keyword) => (
                <Chip key={keyword} label={keyword} onDelete={() => handleDeleteKeyword(keyword, 'th')} />
              ))}
            </Box>

            {/* 中国語キーワード */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>繁體中文</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="關鍵字（繁體中文）"
                value={newKeywordZh}
                onChange={(e) => setNewKeywordZh(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddKeyword('zh');
                  }
                }}
              />
              <Button variant="outlined" onClick={() => handleAddKeyword('zh')} disabled={!newKeywordZh.trim()}>
                追加
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {homepageData.seoKeywordsZh.map((keyword) => (
                <Chip key={keyword} label={keyword} onDelete={() => handleDeleteKeyword(keyword, 'zh')} />
              ))}
            </Box>

            {/* 韓国語キーワード */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>한국어</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="키워드 (한국어)"
                value={newKeywordKo}
                onChange={(e) => setNewKeywordKo(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddKeyword('ko');
                  }
                }}
              />
              <Button variant="outlined" onClick={() => handleAddKeyword('ko')} disabled={!newKeywordKo.trim()}>
                追加
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {homepageData.seoKeywordsKo.map((keyword) => (
                <Chip key={keyword} label={keyword} onDelete={() => handleDeleteKeyword(keyword, 'ko')} />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Google Map */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Googleマップ
            </Typography>

            {/* 住所から自動生成ボタン */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<MapIcon />}
                onClick={() => {
                  const address = formData.fullAddress;
                  if (address) {
                    const encodedAddress = encodeURIComponent(address);
                    const embedUrl = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodedAddress}`;
                    setHomepageData({ ...homepageData, googleMapUrl: embedUrl });
                  }
                }}
                disabled={!formData.fullAddress}
              >
                住所から地図を生成
              </Button>
              <Typography variant="body2" color="text.secondary">
                基本情報タブの住所を使用します
              </Typography>
            </Box>

            {/* 現在の住所を表示 */}
            {formData.fullAddress && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>登録住所：</strong> {formData.fullAddress}
                </Typography>
              </Alert>
            )}

            {/* プレビュー */}
            {homepageData.googleMapUrl && (
              <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                <iframe
                  src={homepageData.googleMapUrl}
                  width="100%"
                  height="250"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Map Preview"
                />
              </Box>
            )}

            <TextField
              fullWidth
              label="GoogleマップURL（直接入力も可能）"
              value={homepageData.googleMapUrl ?? ''}
              onChange={(e) => {
                let value = e.target.value.trim();
                // iframeタグからsrc抽出
                if (value.includes('<iframe')) {
                  const srcMatch = value.match(/src="([^"]+)"/);
                  if (srcMatch && srcMatch[1]) {
                    value = srcMatch[1];
                  }
                }
                // 通常のGoogle Maps URLを埋め込みURLに変換
                if (value && !value.includes('/maps/embed')) {
                  // maps.app.goo.gl 短縮URL or google.com/maps URL → place embed に変換
                  if (value.includes('maps.app.goo.gl') || value.includes('google.com/maps') || value.includes('goo.gl/maps')) {
                    // @lat,lng or place名を抽出できない場合、住所で代替
                    const placeMatch = value.match(/place\/([^\/]+)/);
                    if (placeMatch) {
                      value = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(placeMatch[1].replace(/\+/g, ' '))}`;
                    } else if (formData.fullAddress) {
                      value = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(formData.fullAddress)}`;
                    }
                  }
                }
                setHomepageData({ ...homepageData, googleMapUrl: value });
              }}
              placeholder="https://www.google.com/maps/embed?..."
              helperText="「住所から地図を生成」ボタンを押すか、GoogleマップのiframeタグまたはURLを貼り付けてください。短縮URL(maps.app.goo.gl)は自動で埋め込みURLに変換されます。"
              size="small"
            />
            {homepageData.googleMapUrl && (
              <Button
                size="small"
                color="error"
                onClick={() => setHomepageData({ ...homepageData, googleMapUrl: '' })}
                sx={{ mt: 1 }}
              >
                地図をクリア
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Display Settings */}
          <Typography variant="h6" gutterBottom>
            表示設定
          </Typography>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={homepageData.showBusinessHours}
                  onChange={(e) => setHomepageData({ ...homepageData, showBusinessHours: e.target.checked })}
                />
              }
              label="営業時間を表示"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={homepageData.showPhoneNumber}
                  onChange={(e) => setHomepageData({ ...homepageData, showPhoneNumber: e.target.checked })}
                />
              }
              label="電話番号を表示"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={homepageData.showMenus}
                  onChange={(e) => setHomepageData({ ...homepageData, showMenus: e.target.checked })}
                />
              }
              label="メニュー紹介を表示"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={homepageData.showStaff}
                  onChange={(e) => setHomepageData({ ...homepageData, showStaff: e.target.checked })}
                />
              }
              label="スタッフ紹介を表示"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* カラーテーマ設定 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              カラーテーマ
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ショップページの配色をカスタマイズできます。未設定の場合はデフォルトカラーが使用されます。
            </Typography>

            {/* プリセットテーマ */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>プリセット</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              {([
                { key: 'default', label: 'デフォルト', colors: { primaryColor: '#2563eb', bgDefault: '#f8f9fa', bgWhite: '#ffffff', textPrimary: '#111827', textSecondary: '#6b7280', textMuted: '#9ca3af', borderColor: '#e5e7eb', successColor: '#10b981', warningColor: '#f59e0b', errorColor: '#ef4444' } },
                { key: 'dark', label: 'ダーク', colors: { primaryColor: '#60a5fa', bgDefault: '#111827', bgWhite: '#1f2937', textPrimary: '#f9fafb', textSecondary: '#d1d5db', textMuted: '#9ca3af', borderColor: '#374151', successColor: '#34d399', warningColor: '#fbbf24', errorColor: '#f87171' } },
                { key: 'warm', label: 'ウォーム', colors: { primaryColor: '#dc2626', bgDefault: '#fef2f2', bgWhite: '#ffffff', textPrimary: '#1c1917', textSecondary: '#78716c', textMuted: '#a8a29e', borderColor: '#e7e5e4', successColor: '#16a34a', warningColor: '#ea580c', errorColor: '#dc2626' } },
                { key: 'elegant', label: 'エレガント', colors: { primaryColor: '#7c3aed', bgDefault: '#faf5ff', bgWhite: '#ffffff', textPrimary: '#1e1b4b', textSecondary: '#6b7280', textMuted: '#a78bfa', borderColor: '#e9d5ff', successColor: '#10b981', warningColor: '#f59e0b', errorColor: '#ef4444' } },
              ] as const).map((preset) => (
                <Button
                  key={preset.key}
                  variant="outlined"
                  size="small"
                  onClick={() => setHomepageData({ ...homepageData, themeColors: { ...homepageData.themeColors, ...preset.colors } })}
                  sx={{
                    borderColor: preset.colors.primaryColor,
                    color: preset.colors.primaryColor,
                    '&:hover': { borderColor: preset.colors.primaryColor, bgcolor: preset.colors.bgDefault },
                  }}
                >
                  <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: preset.colors.primaryColor, mr: 1 }} />
                  {preset.label}
                </Button>
              ))}
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={() => setHomepageData({ ...homepageData, themeColors: {} })}
              >
                リセット
              </Button>
            </Box>

            {/* カラーピッカー */}
            <Grid container spacing={2}>
              {([
                { key: 'primaryColor' as const, label: 'メインカラー', defaultVal: '#2563eb' },
                { key: 'bgDefault' as const, label: '背景色', defaultVal: '#f8f9fa' },
                { key: 'bgWhite' as const, label: 'カード背景色', defaultVal: '#ffffff' },
                { key: 'textPrimary' as const, label: 'メイン文字色', defaultVal: '#111827' },
                { key: 'textSecondary' as const, label: 'サブ文字色', defaultVal: '#6b7280' },
                { key: 'textMuted' as const, label: 'ミュート文字色', defaultVal: '#9ca3af' },
                { key: 'borderColor' as const, label: 'ボーダー色', defaultVal: '#e5e7eb' },
                { key: 'successColor' as const, label: '成功色（予約可能）', defaultVal: '#10b981' },
                { key: 'warningColor' as const, label: '警告色', defaultVal: '#f59e0b' },
                { key: 'errorColor' as const, label: 'エラー色', defaultVal: '#ef4444' },
              ]).map((item) => (
                <Grid item xs={6} sm={4} md={3} key={item.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="color"
                      value={homepageData.themeColors?.[item.key] || item.defaultVal}
                      onChange={(e) => setHomepageData({
                        ...homepageData,
                        themeColors: { ...homepageData.themeColors, [item.key]: e.target.value }
                      })}
                      style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>{item.label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {homepageData.themeColors?.[item.key] || item.defaultVal}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* 背景画像 */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>背景画像（任意）</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                背景色の代わりに画像を使用できます
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                  disabled={uploading}
                >
                  画像をアップロード
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !shop) return;
                      setUploading(true);
                      try {
                        const options = { maxSizeMB: 1.0, maxWidthOrHeight: 1920, useWebWorker: true, fileType: 'image/jpeg' as const };
                        const processedFile = await imageCompression(file, options);
                        const fileName = `homepage/${shop.id}/bg_${Date.now()}.jpg`;
                        const storageRef = ref(storage, fileName);
                        await uploadBytes(storageRef, processedFile);
                        const url = await getDownloadURL(storageRef);
                        setHomepageData({ ...homepageData, themeColors: { ...homepageData.themeColors, bgImage: url } });
                      } catch (err: any) {
                        setError(err.message || '背景画像のアップロードに失敗しました');
                      } finally {
                        setUploading(false);
                      }
                    }}
                  />
                </Button>
                {homepageData.themeColors?.bgImage && (
                  <>
                    <Box
                      component="img"
                      src={homepageData.themeColors.bgImage}
                      alt="背景画像"
                      sx={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0' }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setHomepageData({ ...homepageData, themeColors: { ...homepageData.themeColors, bgImage: undefined } })}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>

            {/* プレビュー */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>プレビュー</Typography>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                bgcolor: homepageData.themeColors?.bgDefault || '#f8f9fa',
                ...(homepageData.themeColors?.bgImage ? {
                  backgroundImage: `url(${homepageData.themeColors.bgImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                } : {}),
              }}>
                {/* ヘッダーサンプル */}
                <Box sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 1,
                  bgcolor: homepageData.themeColors?.bgWhite || '#ffffff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: homepageData.themeColors?.primaryColor || '#2563eb' }}>
                    {shop?.shopName || 'ショップ名'}
                  </Typography>
                </Box>
                {/* カードサンプル */}
                <Box sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: homepageData.themeColors?.bgWhite || '#ffffff',
                  border: `1px solid ${homepageData.themeColors?.borderColor || '#e5e7eb'}`,
                }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: homepageData.themeColors?.textPrimary || '#111827' }}>
                    見出しテキスト
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: homepageData.themeColors?.textSecondary || '#6b7280', mt: 0.5 }}>
                    補足テキストがここに表示されます
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: homepageData.themeColors?.textMuted || '#9ca3af', mt: 0.5 }}>
                    ミュートテキスト
                  </Typography>
                </Box>
                {/* ボタンサンプル */}
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  <Box sx={{
                    px: 2, py: 0.5, borderRadius: 1, fontSize: '0.75rem', fontWeight: 'bold',
                    bgcolor: homepageData.themeColors?.primaryColor || '#2563eb', color: '#fff',
                  }}>
                    予約する
                  </Box>
                  <Box sx={{
                    px: 2, py: 0.5, borderRadius: 1, fontSize: '0.75rem',
                    bgcolor: homepageData.themeColors?.successColor || '#10b981', color: '#fff',
                  }}>
                    予約可能
                  </Box>
                  <Box sx={{
                    px: 2, py: 0.5, borderRadius: 1, fontSize: '0.75rem',
                    bgcolor: homepageData.themeColors?.errorColor || '#ef4444', color: '#fff',
                  }}>
                    エラー
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* 商品デフォルト画像 */}
          <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              商品デフォルト画像
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              商品画像が未設定の場合に表示されるデフォルト画像を設定できます。
            </Typography>
            {homepageData.defaultProductImage ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  component="img"
                  src={homepageData.defaultProductImage}
                  alt="デフォルト商品画像"
                  sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0' }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={async () => {
                      if (!shop) return;
                      const updatedData = { ...homepageData, defaultProductImage: '' };
                      const cleanedData = Object.fromEntries(
                        Object.entries(updatedData).filter(([_, v]) => v !== undefined)
                      );
                      const shopRef = doc(db, 'shops', shop.id);
                      await updateDoc(shopRef, { shopHomepage: cleanedData, updatedAt: Timestamp.now() });
                      setHomepageData(updatedData);
                      setSuccess(true);
                    }}
                  >
                    削除
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    component="label"
                  >
                    変更
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file || !shop) return;
                        setUploading(true);
                        try {
                          let processedFile: File | Blob = file;
                          try {
                            processedFile = await imageCompression(file, { maxSizeMB: 0.5, maxWidthOrHeight: 800, useWebWorker: true, fileType: 'image/jpeg' as const });
                          } catch (err) { console.error('圧縮エラー:', err); }
                          const ext = 'jpg';
                          const fileName = `homepage/${shop.id}/product-default_${Date.now()}.${ext}`;
                          const storageRef = ref(storage, fileName);
                          await uploadBytes(storageRef, processedFile);
                          const url = await getDownloadURL(storageRef);
                          const updatedData = { ...homepageData, defaultProductImage: url };
                          const cleanedData = Object.fromEntries(
                            Object.entries(updatedData).filter(([_, v]) => v !== undefined)
                          );
                          const shopRef = doc(db, 'shops', shop.id);
                          await updateDoc(shopRef, { shopHomepage: cleanedData, updatedAt: Timestamp.now() });
                          setHomepageData(updatedData);
                          setSuccess(true);
                        } catch (err: any) {
                          setError(err.message || 'アップロードに失敗しました');
                        } finally {
                          setUploading(false);
                        }
                      }}
                    />
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
              >
                画像をアップロード
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !shop) return;
                    setUploading(true);
                    try {
                      let processedFile: File | Blob = file;
                      try {
                        processedFile = await imageCompression(file, { maxSizeMB: 0.5, maxWidthOrHeight: 800, useWebWorker: true, fileType: 'image/jpeg' as const });
                      } catch (err) { console.error('圧縮エラー:', err); }
                      const ext = 'jpg';
                      const fileName = `homepage/${shop.id}/product-default_${Date.now()}.${ext}`;
                      const storageRef = ref(storage, fileName);
                      await uploadBytes(storageRef, processedFile);
                      const url = await getDownloadURL(storageRef);
                      const updatedData = { ...homepageData, defaultProductImage: url };
                      const cleanedData = Object.fromEntries(
                        Object.entries(updatedData).filter(([_, v]) => v !== undefined)
                      );
                      const shopRef = doc(db, 'shops', shop.id);
                      await updateDoc(shopRef, { shopHomepage: cleanedData, updatedAt: Timestamp.now() });
                      setHomepageData(updatedData);
                      setSuccess(true);
                    } catch (err: any) {
                      setError(err.message || 'アップロードに失敗しました');
                    } finally {
                      setUploading(false);
                    }
                  }}
                />
              </Button>
            )}
          </Box>
        </TabPanel>

        {/* Tab 2: 原価・バック設定 */}
        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                原価・バック機能
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={costBackSettings.enabled}
                    onChange={(e) => setCostBackSettings({ ...costBackSettings, enabled: e.target.checked })}
                  />
                }
                label={costBackSettings.enabled ? "有効" : "無効"}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              この機能を有効にすると、商品ごとに原価とバック率を設定できます。
              バック率とは、販売金額に対して販売スタッフに還元する割合です。
            </Typography>

            {costBackSettings.enabled && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="デフォルトバック率（%）"
                      type="number"
                      value={costBackSettings.defaultBackRate ?? 0}
                      onChange={(e) => setCostBackSettings({
                        ...costBackSettings,
                        defaultBackRate: Math.min(100, Math.max(0, Number(e.target.value)))
                      })}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      helperText="新規商品作成時のデフォルト値"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    分析画面での表示設定
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={costBackSettings.showCostInAnalytics}
                        onChange={(e) => setCostBackSettings({
                          ...costBackSettings,
                          showCostInAnalytics: e.target.checked
                        })}
                      />
                    }
                    label="原価を表示"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={costBackSettings.showBackInAnalytics}
                        onChange={(e) => setCostBackSettings({
                          ...costBackSettings,
                          showBackInAnalytics: e.target.checked
                        })}
                      />
                    }
                    label="バック金額を表示"
                  />
                </Box>
              </Box>
            )}
          </Paper>

          {/* v3.0.0: 売上集計（締日）+ 売上分配 税基準 + 領収書削除保護 */}
          {/* 注文の受け方 */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>注文の受け方</Typography>
            <FormControlLabel
              control={<Switch checked={customerOrderEnabled}
                onChange={(e) => setCustomerOrderEnabled(e.target.checked)} />}
              label="お客様が自分のスマホから注文できるようにする"
            />
            {customerOrderEnabled ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <AlertTitle>キャストのバックが付かない注文になります</AlertTitle>
                お客様からの注文には「どのキャストが取ったか」の情報が付きません。
                そのドリンクは誰のバックにもならず、会計時に「担当未設定」として残ります。
                指名やドリンクバックを運用している店では、オフにしてスタッフが入力してください。
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                スタッフの入力だけで注文を受けます。お客様の画面では、
                メニューの閲覧と自分の伝票の確認はできますが、注文ボタンは出ません。
                スタッフは「代理注文」から卓を選んで入力します。
              </Alert>
            )}
          </Paper>

          {/* プランと課金（伝票数で決まる） */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>プランと課金</Typography>
            {(() => {
              const gate = evaluatePlan(shop?.planTier as any, planUsage);
              return (
                <>
                  <Alert severity={gate.overLimit ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    <AlertTitle>
                      {PLAN_LABELS[gate.tier]}
                      {gate.overLimit ? '（上限を超えています）' : ''}
                    </AlertTitle>
                    {gate.message}
                  </Alert>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">当月の伝票</Typography>
                      <Typography variant="h6">{planUsage.slipsThisMonth.toLocaleString()} 件</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">直近3ヶ月の最大</Typography>
                      <Typography variant="h6">{(planUsage.peakMonthlySlips ?? 0).toLocaleString()} 件</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">上限</Typography>
                      <Typography variant="h6">
                        {gate.limits.maxSlipsPerMonth === null ? 'なし' : `${gate.limits.maxSlipsPerMonth.toLocaleString()} 件`}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">従業員</Typography>
                      <Typography variant="h6">{planUsage.employeeCount} 名</Typography>
                    </Grid>
                  </Grid>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    課金は<strong>伝票数（会計の件数）</strong>だけで決まります。
                    従業員の人数、PC かアプリか、といった条件はありません。
                    機能の制限もどのプランでも同じです。
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    上限を超えても<strong>会計は止まりません</strong>。営業中に会計できなくなるほうが事故なので、
                    超過は警告として出し、請求で精算します。
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {(['free', 'standard', 'pro'] as const).map((t) => (
                      <Grid item xs={12} sm={4} key={t}>
                        <Paper variant="outlined" sx={{
                          p: 2,
                          borderColor: gate.tier === t ? 'primary.main' : undefined,
                          borderWidth: gate.tier === t ? 2 : 1,
                        }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {PLAN_LABELS[t]}{gate.tier === t ? '（現在）' : ''}
                          </Typography>
                          <Typography variant="h6" color={t === 'free' ? 'text.primary' : 'primary.main'}>
                            {TIER_DESCRIPTIONS[t].price}
                          </Typography>
                          <Typography variant="body2">{TIER_DESCRIPTIONS[t].slips}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {TIER_DESCRIPTIONS[t].note}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </>
              );
            })()}
          </Paper>

          {/* v4.0.0: 伝票の取消 */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>伝票の取消</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>伝票は「消す」のではなく「取り消す」</AlertTitle>
              取り消しても<strong>伝票番号は欠番になりません</strong>。その番号のまま「取消済み」として残り、
              売上集計からだけ外れます。誰が・いつ・なぜ取り消したかは、変更できない記録として残ります。
              番号が連続していれば、取消は日常の操作として説明できます。
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small" label="取消できる役割"
                  value={voidPolicy.allowedRoles.includes('staff') ? 'staff' : 'manager'}
                  onChange={(e) => setVoidPolicy({
                    ...voidPolicy,
                    allowedRoles: e.target.value === 'staff'
                      ? ['owner', 'manager', 'staff']
                      : ['owner', 'manager'],
                  })}
                >
                  <MenuItem value="manager">オーナー・責任者のみ</MenuItem>
                  <MenuItem value="staff">スタッフも可（承認を推奨）</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small" label="取消の期限"
                  value={voidPolicy.timeLimitMinutes === null ? 'none' : String(voidPolicy.timeLimitMinutes)}
                  onChange={(e) => setVoidPolicy({
                    ...voidPolicy,
                    timeLimitMinutes: e.target.value === 'none' ? null : Number(e.target.value),
                  })}
                  helperText="会計からこの時間を過ぎると取り消せなくなります"
                >
                  <MenuItem value="none">制限なし</MenuItem>
                  <MenuItem value="30">30分</MenuItem>
                  <MenuItem value="60">1時間</MenuItem>
                  <MenuItem value="360">6時間（同じ営業日のうち）</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Stack sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Switch checked={voidPolicy.requireApproval}
                  onChange={(e) => setVoidPolicy({ ...voidPolicy, requireApproval: e.target.checked })} />}
                label="責任者の承認を必要にする（オーナー以外が取り消すとき）"
              />
              <FormControlLabel
                control={<Switch checked={voidPolicy.requireNote}
                  onChange={(e) => setVoidPolicy({ ...voidPolicy, requireNote: e.target.checked })} />}
                label="理由の記入を必須にする"
              />
              <FormControlLabel
                control={<Switch checked={voidPolicy.allowAfterReceipt}
                  onChange={(e) => setVoidPolicy({ ...voidPolicy, allowAfterReceipt: e.target.checked })} />}
                label="領収書を発行済みの伝票も取り消せるようにする"
              />
              <FormControlLabel
                control={<Switch checked={voidPolicy.allowAfterDailyClose}
                  onChange={(e) => setVoidPolicy({ ...voidPolicy, allowAfterDailyClose: e.target.checked })} />}
                label="締め済みの営業日も取り消せるようにする"
              />
            </Stack>
            {(voidPolicy.allowAfterReceipt || voidPolicy.allowAfterDailyClose) && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {voidPolicy.allowAfterReceipt && '領収書発行済みを取り消すと、お客様の控えと店の記録が食い違います。'}
                {voidPolicy.allowAfterDailyClose && ' 締め後の取消は日次売上を作り直すことになります。'}
                どちらも操作時に警告が出ますが、記録は必ず残ります。
              </Alert>
            )}
          </Paper>

          {/* v4.0.0: バックの調整 */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>バックの調整</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>自動計算の値は上書きされません</AlertTitle>
              調整は「元の額 ＋ 増減」として積まれます。訂正・手当・控除のどれなのか、
              いくら動かしたのか、理由は何かが残るので、
              キャスト本人に聞かれたときにそのまま説明できます。
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small" type="number" label="1件あたりの上限（円）"
                  value={adjustmentPolicy.maxDeltaPerAdjustment ?? ''}
                  onChange={(e) => setAdjustmentPolicy({
                    ...adjustmentPolicy,
                    maxDeltaPerAdjustment: e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
                  })}
                  helperText="空欄で制限なし"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small" label="自動計算額に対する上限"
                  value={adjustmentPolicy.maxRatioOfCalculated === null ? 'none' : String(adjustmentPolicy.maxRatioOfCalculated)}
                  onChange={(e) => setAdjustmentPolicy({
                    ...adjustmentPolicy,
                    maxRatioOfCalculated: e.target.value === 'none' ? null : Number(e.target.value),
                  })}
                  helperText="大きく動かすときに気づけるようにする"
                >
                  <MenuItem value="none">制限なし</MenuItem>
                  <MenuItem value="0.2">±20% まで</MenuItem>
                  <MenuItem value="0.5">±50% まで</MenuItem>
                  <MenuItem value="1">±100% まで</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Stack sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Switch checked={adjustmentPolicy.requireReason}
                  onChange={(e) => setAdjustmentPolicy({ ...adjustmentPolicy, requireReason: e.target.checked })} />}
                label="理由の記入を必須にする"
              />
              <FormControlLabel
                control={<Switch checked={adjustmentPolicy.visibleToStaff}
                  onChange={(e) => setAdjustmentPolicy({ ...adjustmentPolicy, visibleToStaff: e.target.checked })} />}
                label="キャスト本人のアプリに調整の内訳を表示する"
              />
              <FormControlLabel
                control={<Switch checked={adjustmentPolicy.allowAfterPeriodClose}
                  onChange={(e) => setAdjustmentPolicy({ ...adjustmentPolicy, allowAfterPeriodClose: e.target.checked })} />}
                label="締め済みの給与期間も調整できるようにする"
              />
            </Stack>
            {!adjustmentPolicy.visibleToStaff && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                内訳を隠すと、キャストのアプリには支給額だけが出ます。
                減額したときに理由を聞かれても画面では答えられないので、口頭で説明できるようにしてください。
              </Alert>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              売上集計・分配の店舗ポリシー（v3.0.0）
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                売上の締日
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                月次売上集計・累進ティアバック計算の期間境界。例: 15日締め → 毎月16日〜翌15日。
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={salesPeriodClosingDay === 'monthEnd' ? 'monthEnd' : String(salesPeriodClosingDay)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSalesPeriodClosingDay(v === 'monthEnd' ? 'monthEnd' : parseInt(v, 10));
                  }}
                >
                  <MenuItem value="monthEnd">月末締め</MenuItem>
                  {[5, 10, 15, 20, 25, 28, 30, 31].map((d) => (
                    <MenuItem key={d} value={String(d)}>{d}日締め</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 1, max: 31 }}
                label="任意の締日 (1-31)"
                value={typeof salesPeriodClosingDay === 'number' ? salesPeriodClosingDay : ''}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!isNaN(n) && n >= 1 && n <= 31) setSalesPeriodClosingDay(n);
                }}
                sx={{ mt: 1, width: 180 }}
                helperText="一覧に無い日を使う場合"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                30日・31日締めは、その日が無い月（2月など）は月末が締日になります。
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                勤怠の判定
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                シフト開始からこの分数を過ぎたら遅刻として記録します。日を跨ぐシフトでも正しく判定されます。
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth size="small" type="number" label="遅刻の猶予（分）"
                    value={attendanceSettings.lateGraceMinutes}
                    onChange={(e) => setAttendanceSettings({
                      ...attendanceSettings,
                      lateGraceMinutes: Math.max(0, Number(e.target.value)),
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth size="small" type="number" label="早退の猶予（分）"
                    value={attendanceSettings.earlyLeaveGraceMinutes}
                    onChange={(e) => setAttendanceSettings({
                      ...attendanceSettings,
                      earlyLeaveGraceMinutes: Math.max(0, Number(e.target.value)),
                    })}
                  />
                </Grid>
              </Grid>

            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                売上分配の税基準
              </Typography>
              <RadioGroup
                row
                value={salesAttributionTaxBase}
                onChange={(e) => setSalesAttributionTaxBase(e.target.value as any)}
              >
                <FormControlLabel value="taxExcluded" control={<Radio />} label="税抜ベース（既定）" />
                <FormControlLabel value="taxIncluded" control={<Radio />} label="税込ベース" />
              </RadioGroup>
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={receiptDeleteProtection}
                    onChange={(e) => setReceiptDeleteProtection(e.target.checked)}
                  />
                }
                label="領収書発行済み伝票の削除を禁止する"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                ON: 印刷 or デジタル送信済みの伝票は削除不可（オーナー画面で★表示）
              </Typography>
            </Box>
          </Paper>

          {/* インセンティブ設定 */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                売上全体ベースのインセンティブ（上級者向け）
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={incentiveSettings.enabled}
                    onChange={(e) => setIncentiveSettings({ ...incentiveSettings, enabled: e.target.checked })}
                  />
                }
                label={incentiveSettings.enabled ? "有効" : "無効"}
              />
            </Box>
            <Box
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'warning.lighter',
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ⚠️ ご注意: ほとんどの店舗ではこの機能を「無効」のままで OK です
              </Typography>
              <Typography variant="body2" color="text.secondary">
                通常はバー・スナック等で「シャンパンやスタッフドリンクのみ」スタッフにバックする運用が一般的です。
                その場合は<strong>「原価・バック機能」（商品単位）</strong>のみで設定してください。
                <br />
                この機能は<strong>「会計全体に対して % で還元」</strong>する設定で、商品単位バックと
                <strong>同時に有効化すると二重計上</strong>になる可能性があります。
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              売上全体に対してスタッフにインセンティブを付与します。
              小計（税・サービス料・カード手数料除外）をベースに、雇用形態別のレートで自動計算されます。
            </Typography>

            {incentiveSettings.enabled && (
              <Box sx={{ mt: 3 }}>
                {/* 累進の当て方 */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  累進の当て方
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="計算方式"
                      value={incentiveSettings.mode}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        mode: e.target.value as 'retroactive' | 'marginal' | 'wholeAtTier',
                      })}
                      helperText="会計を分けても総額が変わらない方式を推奨します"
                    >
                      <MenuItem value="retroactive">累計全体に当てる（推奨）</MenuItem>
                      <MenuItem value="marginal">帯ごとに当てる</MenuItem>
                      <MenuItem value="wholeAtTier">旧方式（会計ごと・分割で減る）</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Alert severity={incentiveSettings.mode === 'wholeAtTier' ? 'warning' : 'info'} sx={{ height: '100%' }}>
                      {incentiveSettings.mode === 'retroactive' &&
                        '当期累計で決めたレートを累計全体に当て、既払いとの差額を支払います。月商100万超なら全体が25%、という運用そのままです。'}
                      {incentiveSettings.mode === 'marginal' &&
                        '各ティアの帯に入った金額だけへ、その帯のレートを当てます。所得税の累進と同じ考え方です。'}
                      {incentiveSettings.mode === 'wholeAtTier' &&
                        '会計ごとにその時点のレートを当てます。同じ月商でも伝票を分けるほどバックが減るため、互換目的以外では推奨しません。'}
                    </Alert>
                  </Grid>
                </Grid>

                {/* アルバイトルール */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  アルバイト（時給スタッフ）ルール
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  1日あたりの平均小計に基づいてレートを判定します。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="1日の想定勤務時間"
                      type="number"
                      value={incentiveSettings.partTimeRules.standardDailyHours}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        partTimeRules: {
                          ...incentiveSettings.partTimeRules,
                          standardDailyHours: Math.max(1, Number(e.target.value)),
                        }
                      })}
                      helperText="「時給未満」の判定に使う。夜職は5〜6時間が目安"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="日平均小計の閾値（円）"
                      type="number"
                      value={incentiveSettings.partTimeRules.dailyAvgThreshold}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        partTimeRules: { ...incentiveSettings.partTimeRules, dailyAvgThreshold: Number(e.target.value) }
                      })}
                      inputProps={{ min: 0, step: 1000 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="閾値以下レート（%）"
                      type="number"
                      value={incentiveSettings.partTimeRules.lowRate}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        partTimeRules: { ...incentiveSettings.partTimeRules, lowRate: Math.min(100, Math.max(0, Number(e.target.value))) }
                      })}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      size="small"
                      helperText={`日平均 ≤ ¥${incentiveSettings.partTimeRules.dailyAvgThreshold.toLocaleString()}`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="閾値超えレート（%）"
                      type="number"
                      value={incentiveSettings.partTimeRules.highRate}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        partTimeRules: { ...incentiveSettings.partTimeRules, highRate: Math.min(100, Math.max(0, Number(e.target.value))) }
                      })}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      size="small"
                      helperText={`日平均 > ¥${incentiveSettings.partTimeRules.dailyAvgThreshold.toLocaleString()}`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="時給未満レート（%）"
                      type="number"
                      value={incentiveSettings.partTimeRules.belowWageRate}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        partTimeRules: { ...incentiveSettings.partTimeRules, belowWageRate: Math.min(100, Math.max(0, Number(e.target.value))) }
                      })}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      size="small"
                      helperText="売上小計 < 支給時給の場合"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* レギュラースタッフルール */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  レギュラースタッフ（月給スタッフ）ルール
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  月間小計の累計に基づいてレートを判定します。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="月給以下レート（%）"
                      type="number"
                      value={incentiveSettings.regularRules.belowSalaryRate}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        regularRules: { ...incentiveSettings.regularRules, belowSalaryRate: Math.min(100, Math.max(0, Number(e.target.value))) }
                      })}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      size="small"
                      helperText="月間小計 ≤ 月給"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="月給超えレート（%）"
                      type="number"
                      value={incentiveSettings.regularRules.aboveSalaryRate}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        regularRules: { ...incentiveSettings.regularRules, aboveSalaryRate: Math.min(100, Math.max(0, Number(e.target.value))) }
                      })}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      size="small"
                      helperText="月間小計 > 月給"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="ティア1 閾値（円）"
                      type="number"
                      value={incentiveSettings.regularRules.tier1Threshold}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        regularRules: { ...incentiveSettings.regularRules, tier1Threshold: Number(e.target.value) }
                      })}
                      inputProps={{ min: 0, step: 10000 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="ティア1 レート（%）"
                      type="number"
                      value={incentiveSettings.regularRules.tier1Rate}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        regularRules: { ...incentiveSettings.regularRules, tier1Rate: Math.min(100, Math.max(0, Number(e.target.value))) }
                      })}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      size="small"
                      helperText={`月間小計 > ¥${incentiveSettings.regularRules.tier1Threshold.toLocaleString()}`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="ティア2 閾値（円）"
                      type="number"
                      value={incentiveSettings.regularRules.tier2Threshold}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        regularRules: { ...incentiveSettings.regularRules, tier2Threshold: Number(e.target.value) }
                      })}
                      inputProps={{ min: 0, step: 10000 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="ティア2 レート（%）"
                      type="number"
                      value={incentiveSettings.regularRules.tier2Rate}
                      onChange={(e) => setIncentiveSettings({
                        ...incentiveSettings,
                        regularRules: { ...incentiveSettings.regularRules, tier2Rate: Math.min(100, Math.max(0, Number(e.target.value))) }
                      })}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      size="small"
                      helperText={`月間小計 > ¥${incentiveSettings.regularRules.tier2Threshold.toLocaleString()}`}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* シフト自動公開設定 */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">シフト自動公開</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={shiftAutoPublish.enabled}
                    onChange={(e) => setShiftAutoPublish({ ...shiftAutoPublish, enabled: e.target.checked })}
                  />
                }
                label={shiftAutoPublish.enabled ? "有効" : "無効"}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              テンプレートに基づいてシフトを自動生成し、スタッフに通知します
            </Typography>

            {shiftAutoPublish.enabled && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>スケジュール</InputLabel>
                      <Select
                        value={shiftAutoPublish.scheduleType ?? 'weekly'}
                        label="スケジュール"
                        onChange={(e) => setShiftAutoPublish({ ...shiftAutoPublish, scheduleType: e.target.value as any })}
                      >
                        <MenuItem value="weekly">毎週</MenuItem>
                        <MenuItem value="biweekly">隔週</MenuItem>
                        <MenuItem value="specific_day">毎月x日</MenuItem>
                        <MenuItem value="month_end">月末</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {(shiftAutoPublish.scheduleType === 'weekly' || shiftAutoPublish.scheduleType === 'biweekly') && (
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>曜日</InputLabel>
                        <Select
                          value={shiftAutoPublish.dayOfWeek ?? 1}
                          label="曜日"
                          onChange={(e) => setShiftAutoPublish({ ...shiftAutoPublish, dayOfWeek: Number(e.target.value) })}
                        >
                          <MenuItem value={1}>月曜日</MenuItem>
                          <MenuItem value={2}>火曜日</MenuItem>
                          <MenuItem value={3}>水曜日</MenuItem>
                          <MenuItem value={4}>木曜日</MenuItem>
                          <MenuItem value={5}>金曜日</MenuItem>
                          <MenuItem value={6}>土曜日</MenuItem>
                          <MenuItem value={0}>日曜日</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {shiftAutoPublish.scheduleType === 'specific_day' && (
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="日にち"
                        type="number"
                        value={shiftAutoPublish.dayOfMonth}
                        onChange={(e) => setShiftAutoPublish({ ...shiftAutoPublish, dayOfMonth: Math.min(28, Math.max(1, Number(e.target.value))) })}
                        inputProps={{ min: 1, max: 28 }}
                        size="small"
                        helperText="1〜28日"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="生成期間（日数）"
                      type="number"
                      value={shiftAutoPublish.generateDays}
                      onChange={(e) => setShiftAutoPublish({ ...shiftAutoPublish, generateDays: Math.max(1, Number(e.target.value)) })}
                      inputProps={{ min: 1, max: 31 }}
                      size="small"
                      helperText="何日先まで生成するか"
                    />
                  </Grid>
                </Grid>

                <FormControlLabel
                  control={
                    <Switch
                      checked={shiftAutoPublish.notifyStaff}
                      onChange={(e) => setShiftAutoPublish({ ...shiftAutoPublish, notifyStaff: e.target.checked })}
                      size="small"
                    />
                  }
                  label="シフト公開時にスタッフへLINE通知を送信"
                />
              </Box>
            )}
          </Paper>

          {/* スタッフアプリ設定 */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                スタッフアプリ設定
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2">シフト変更・休暇希望</Typography>
                <Typography variant="body2" color="text.secondary">
                  スタッフがアプリからシフトの交代希望や休み希望を出せるようにします
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={staffAppSettings.shiftChangeRequestEnabled}
                    onChange={(e) => setStaffAppSettings({ ...staffAppSettings, shiftChangeRequestEnabled: e.target.checked })}
                  />
                }
                label={staffAppSettings.shiftChangeRequestEnabled ? "有効" : "無効"}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2">売上分析の表示</Typography>
                <Typography variant="body2" color="text.secondary">
                  一般スタッフのアプリに売上分析画面を表示します
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={staffAppSettings.showAnalyticsToStaff}
                    onChange={(e) => setStaffAppSettings({ ...staffAppSettings, showAnalyticsToStaff: e.target.checked })}
                  />
                }
                label={staffAppSettings.showAnalyticsToStaff ? "表示" : "非表示"}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Box>
                <Typography variant="subtitle2">バック・インセンティブ明細の表示</Typography>
                <Typography variant="body2" color="text.secondary">
                  一般スタッフのアプリにバック／インセンティブ明細タブを表示します
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={staffAppSettings.showBackToStaff}
                    onChange={(e) => setStaffAppSettings({ ...staffAppSettings, showBackToStaff: e.target.checked })}
                  />
                }
                label={staffAppSettings.showBackToStaff ? "表示" : "非表示"}
              />
            </Box>

            {(staffAppSettings.showAnalyticsToStaff || staffAppSettings.showBackToStaff) && (
            <>
            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle2">売上データの閲覧期間制限</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                上記で表示ONの場合、一般スタッフが閲覧できるデータの期間を制限できます（オーナー・マネージャーは制限なし）
              </Typography>
              <TextField
                select
                size="small"
                value={staffAppSettings.salesVisibilityDays}
                onChange={(e) => setStaffAppSettings({ ...staffAppSettings, salesVisibilityDays: Number(e.target.value) })}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value={0}>制限なし</MenuItem>
                <MenuItem value={7}>過去7日間</MenuItem>
                <MenuItem value={14}>過去14日間</MenuItem>
                <MenuItem value={30}>過去30日間</MenuItem>
                <MenuItem value={60}>過去60日間</MenuItem>
                <MenuItem value={90}>過去90日間</MenuItem>
              </TextField>
            </Box>
            </>
            )}
          </Paper>
        </TabPanel>

        {/* Tab 3: 支払い方法 */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6">支払い方法マスタ</Typography>
              <Typography variant="body2" color="text.secondary">
                会計時に選択できる支払い方法を設定します
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenPaymentMethodDialog()}
            >
              追加
            </Button>
          </Box>

          {paymentMethods.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography color="text.secondary">
                支払い方法が登録されていません
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenPaymentMethodDialog()}
                sx={{ mt: 2 }}
              >
                最初の支払い方法を追加
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50}>順序</TableCell>
                    <TableCell>種別</TableCell>
                    <TableCell>名称</TableCell>
                    <TableCell>コード</TableCell>
                    <TableCell align="center">手数料</TableCell>
                    <TableCell align="center">状態</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentMethods
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((method, index) => (
                      <TableRow key={method.id} sx={{ opacity: method.isActive ? 1 : 0.5 }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleMovePaymentMethod(index, 'up')}
                              disabled={index === 0}
                            >
                              ▲
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMovePaymentMethod(index, 'down')}
                              disabled={index === paymentMethods.length - 1}
                            >
                              ▼
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getPaymentTypeIcon(method.type)}
                            <Typography variant="body2">{getPaymentTypeLabel(method.type)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="bold">{method.name}</Typography>
                          {(method.nameEn || method.nameTh) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {[method.nameEn, method.nameTh].filter(Boolean).join(' / ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={method.code} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          {method.feeRate ? `${(method.feeRate * 100).toFixed(1)}%` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={method.isActive}
                            onChange={() => handleTogglePaymentMethod(method.id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenPaymentMethodDialog(method)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeletePaymentMethod(method.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              よく使われる支払い方法の例：
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              • 現金（cash）- 現金支払い<br />
              • クレジットカード（card）- VISA、MasterCard等<br />
              • PayPay（paypay）- QRコード決済<br />
              • PromptPay（promptpay）- タイのQR決済<br />
              • 銀行振込（transfer）- 銀行振込
            </Typography>
          </Alert>
        </TabPanel>

        {/* Tab 4: Business Hours */}
        <TabPanel value={tabValue} index={4}>
          <BusinessHoursV2
            businessSchedule={businessSchedule}
            onChange={setBusinessSchedule}
          />

          <Box sx={{ mt: 3 }}>
            <BusinessCalendar
              businessCalendar={businessCalendar}
              onChange={(calendar) => setBusinessCalendar(calendar)}
            />
          </Box>
        </TabPanel>

      </Paper>

      {/* Image Size Selection Dialog */}
      <Dialog open={showSizeDialog} onClose={() => setShowSizeDialog(false)}>
        <DialogTitle>画像サイズを選択</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 1 }}>
            <FormLabel component="legend">アップロードサイズ</FormLabel>
            <RadioGroup
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value as 'original' | 'standard' | 'high')}
            >
              <FormControlLabel
                value="standard"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">標準（推奨） - 800px幅</Typography>
                    <Typography variant="caption" color="text.secondary">スマホ表示に最適化。容量：約500KB以下</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="high"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">高品質 - 1200px幅</Typography>
                    <Typography variant="caption" color="text.secondary">タブレット・PC表示にも対応。容量：約1MB以下</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="original"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">元のサイズ（圧縮なし）</Typography>
                    <Typography variant="caption" color="text.secondary">撮影時のサイズそのまま</Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
          {selectedFiles && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                選択中: {selectedFiles.length}枚
              </Typography>
              {Array.from(selectedFiles).map((file, index) => (
                <Typography key={index} variant="caption" display="block" color="text.secondary">
                  {file.name} ({(file.size / 1024 / 1024).toFixed(2)}MB)
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSizeDialog(false)}>キャンセル</Button>
          <Button onClick={handleConfirmSizeAndUpload} variant="contained" color="primary">
            アップロード開始
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Method Dialog */}
      <Dialog open={paymentMethodDialogOpen} onClose={() => setPaymentMethodDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPaymentMethod?.id.startsWith('pm_') && !paymentMethods.find(m => m.id === editingPaymentMethod?.id)
            ? '支払い方法を追加'
            : '支払い方法を編集'}
        </DialogTitle>
        <DialogContent>
          {editingPaymentMethod && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="名称（日本語）"
                  value={editingPaymentMethod.name}
                  onChange={(e) => setEditingPaymentMethod({ ...editingPaymentMethod, name: e.target.value })}
                  required
                  placeholder="例: 現金、PayPay、クレジットカード"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="名称（English）"
                  value={editingPaymentMethod.nameEn || ''}
                  onChange={(e) => setEditingPaymentMethod({ ...editingPaymentMethod, nameEn: e.target.value })}
                  placeholder="例: Cash, PayPay"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="名称（ไทย）"
                  value={editingPaymentMethod.nameTh || ''}
                  onChange={(e) => setEditingPaymentMethod({ ...editingPaymentMethod, nameTh: e.target.value })}
                  placeholder="例: เงินสด"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="コード"
                  value={editingPaymentMethod.code}
                  onChange={(e) => setEditingPaymentMethod({ ...editingPaymentMethod, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  required
                  placeholder="例: cash, paypay, card"
                  helperText="半角英数字とアンダースコアのみ"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="種別"
                  value={editingPaymentMethod.type}
                  onChange={(e) => setEditingPaymentMethod({ ...editingPaymentMethod, type: e.target.value as PaymentMethodSetting['type'] })}
                >
                  <MenuItem value="cash">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CashIcon /> 現金
                    </Box>
                  </MenuItem>
                  <MenuItem value="card">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CardIcon /> カード
                    </Box>
                  </MenuItem>
                  <MenuItem value="qr">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QrIcon /> QR決済
                    </Box>
                  </MenuItem>
                  <MenuItem value="transfer">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TransferIcon /> 振込
                    </Box>
                  </MenuItem>
                  <MenuItem value="other">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <OtherIcon /> その他
                    </Box>
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="決済手数料 (%)"
                  type="number"
                  value={editingPaymentMethod.feeRate ? (editingPaymentMethod.feeRate * 100).toString() : '0'}
                  onChange={(e) => setEditingPaymentMethod({ ...editingPaymentMethod, feeRate: (parseFloat(e.target.value) || 0) / 100 })}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  helperText="0%で手数料なし（例: カード10%、PayPay10%）"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingPaymentMethod.isActive}
                      onChange={(e) => setEditingPaymentMethod({ ...editingPaymentMethod, isActive: e.target.checked })}
                    />
                  }
                  label="有効"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentMethodDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSavePaymentMethod}
            disabled={!editingPaymentMethod?.name || !editingPaymentMethod?.code}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table Edit Dialog */}
      <Dialog open={tableDialogOpen} onClose={() => setTableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTable?.id ? 'テーブルを編集' : 'テーブルを追加'}
        </DialogTitle>
        <DialogContent>
          {editingTable && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="テーブル番号"
                  type="number"
                  value={editingTable.tableNumber}
                  onChange={(e) => setEditingTable({ ...editingTable, tableNumber: parseInt(e.target.value) || 1 })}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="定員（名）"
                  type="number"
                  value={editingTable.capacity}
                  onChange={(e) => setEditingTable({ ...editingTable, capacity: parseInt(e.target.value) || 1 })}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="テーブル名"
                  value={editingTable.tableName}
                  onChange={(e) => setEditingTable({ ...editingTable, tableName: e.target.value })}
                  required
                  placeholder="例: テーブル1、カウンターA、個室1"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="備考"
                  value={editingTable.description || ''}
                  onChange={(e) => setEditingTable({ ...editingTable, description: e.target.value })}
                  placeholder="例: 窓際、喫煙可、VIPルーム"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSaveTable}
            disabled={!editingTable?.tableName || tablesLoading}
            startIcon={tablesLoading ? <CircularProgress size={20} /> : null}
          >
            {tablesLoading ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShopSettingsUnified;
